import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Save, Star, Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff,
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight,
  Image as ImageIcon, Type, Droplets, FileText, Loader2, Stamp,
  Users, Table as TableIcon, Lock, Unlock, ChevronsUp, ChevronsDown, Layers,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { fontCss, refreshCustomFonts } from "../lib/fonts";
import { FontSelect } from "../components/FontSelect";
import { FontLibrary } from "../components/FontLibrary";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const SIZES = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 96, 120, 144];

const clampSize = (v) => Math.min(144, Math.max(6, Number(v) || 6));

const DEFAULT_GS_ROWS = [["A+", "Above 85%"], ["A", "75% - 84%"], ["B+", "65% - 74%"], ["B", "50% - 64%"], ["C", "Below 50%"]];

const EX_DEFAULTS = {
  percentage: "Percentage : {value}%",
  grade: "Grade : '{value}'",
  result: "Result : {value}",
  prepared_by: "Prepared by",
  signatory: "Authorized Signatory",
  certificate_no: "Certificate No. : {value}",
  issue_date: "Issue Date : {value}",
};

const ELEMENT_LABELS = {
  institute_name: "Institute / Society Name",
  registration_info: "Registration / Act Information",
  address: "Address",
  contact: "Contact Details",
  email: "Email / Verification Email",
};

/* ---- body layout: student details + marks table (data-bound, design only) ---- */

const FIELD_LABELS = {
  student_name: "Student Name",
  father_name: "Father's Name",
  mother_name: "Mother's Name",
  session: "Session",
  enrollment_no: "Enrollment Number",
  registration_no: "Registration Number",
  course_name: "Course Name",
  course_duration: "Course Duration",
  certificate_no: "Certificate Number",
  issue_date: "Issue Date",
  institute_name: "Institute / Study Centre",
  grade: "Grade",
  line_certify: "Line: 'This is to Certified that'",
  line_completed: "Line: 'Has successfully completed'",
};

const F1_FIELD_ORDER = [
  "enrollment_no", "registration_no", "line_certify", "student_name",
  "father_name", "mother_name", "line_completed", "course_name",
  "course_duration", "grade", "session", "institute_name",
  "certificate_no", "issue_date",
];
const F2_FIELD_ORDER = [
  "enrollment_no", "registration_no", "course_name", "student_name",
  "father_name", "session", "institute_name", "course_duration",
];

function defaultField(x, y, extra = {}) {
  return {
    visible: true, locked: false, text: "", font_family: "serif", size: 12,
    bold: false, italic: false, underline: false, color: "#111827",
    align: "center", x, y, bg_color: "", bg_opacity: 100,
    letter_spacing: 0, padding: 0, ...extra,
  };
}

function defaultBodyF1() {
  return {
    student_block: { visible: true, locked: false, dx: 0, dy: 0, width: 560, height: 320, z: 1, margin: 0, padding: 0 },
    fields: {
      enrollment_no: defaultField(5.7, 152, { size: 10.5, bold: true, align: "left" }),
      registration_no: defaultField(94.3, 152, { size: 10.5, bold: true, align: "right" }),
      line_certify: defaultField(50, 232, { size: 14, italic: true, color: "#4b5563" }),
      student_name: defaultField(50, 266, { size: 30, bold: true }),
      father_name: defaultField(44, 292, { size: 12.5, align: "right" }),
      mother_name: defaultField(56, 292, { size: 12.5, align: "left" }),
      line_completed: defaultField(50, 330, { size: 14, color: "#4b5563" }),
      course_name: defaultField(50, 358, { size: 22, bold: true, color: "#1e3a8a" }),
      course_duration: defaultField(50, 386, { size: 13 }),
      grade: defaultField(50, 408, { size: 13, bold: true }),
      session: defaultField(50, 428, { size: 12.5, color: "#4b5563" }),
      institute_name: defaultField(50, 448, { size: 12.5 }),
      certificate_no: defaultField(5.7, 525, { size: 10, align: "left", color: "#4b5563" }),
      issue_date: defaultField(5.7, 540, { size: 10, align: "left", color: "#4b5563" }),
    },
  };
}

function defaultBodyF2() {
  return {
    student_block: { visible: true, locked: false, dx: 0, dy: 0, width: 507, height: 110, z: 1, margin: 0, padding: 0 },
    fields: {
      enrollment_no: defaultField(6.7, 138, { size: 10, bold: true, align: "left" }),
      registration_no: defaultField(93.3, 138, { size: 10, bold: true, align: "right" }),
      course_name: defaultField(7.4, 190, { size: 10.5, align: "left" }),
      student_name: defaultField(7.4, 207, { size: 10.5, align: "left", bold: true }),
      father_name: defaultField(7.4, 224, { size: 10.5, align: "left" }),
      session: defaultField(52.6, 190, { size: 10.5, align: "left" }),
      institute_name: defaultField(52.6, 207, { size: 10.5, align: "left" }),
      course_duration: defaultField(52.6, 224, { size: 10.5, align: "left" }),
    },
    title_bar: {
      visible: true, locked: false, text: "", font_family: "serif", size: 14,
      bold: true, italic: false, underline: false, color: "#ffffff",
      align: "center", x: 50, y: 147, width: 260, height: 26,
      bg_color: "#1e3a8a", bg_opacity: 100, letter_spacing: 0, padding: 4,
      border_radius: 4, z: 2,
    },
    marks_table: {
      visible: true, locked: false, x: 50, y: 250, width: 507,
      row_height: 26, cell_padding: 6, z: 2, table_bg: "",
      header: { font_family: "serif", size: 10, bold: true, color: "#ffffff", bg: "#1e3a8a", align: "center" },
      cell: { font_family: "serif", size: 10, bold: false, color: "#111827", align: "left", bg: "" },
      total_row: { bg: "#e8eef9" },
      borders: { color: "#334155", thickness: 0.6, style: "solid", outer: true, inner: true },
      columns: [
        { key: "subject", label: "SUBJECT", width: 240, visible: true, header_align: "left", cell_align: "left" },
        { key: "total", label: "TOTAL", width: 80, visible: true, header_align: "center", cell_align: "center" },
        { key: "min", label: "MIN. MARKS", width: 90, visible: true, header_align: "center", cell_align: "center" },
        { key: "obtained", label: "MARKS OBTAINED", width: 97, visible: true, header_align: "center", cell_align: "center" },
      ],
    },
    term_tables: [1, 2].map((term, i) => ({
      id: `tt${term}`,
      term,
      visible: true,
      locked: false,
      title: {
        visible: true, locked: false, text: "", font_family: "serif", size: 13,
        bold: true, italic: false, underline: false, color: "#1e3a8a",
        align: "left", x: 7.4, y: 238 + i * 190, bg_color: "", bg_opacity: 100,
        letter_spacing: 1, padding: 0, z: 2,
      },
      table: {
        visible: true, locked: false, x: 50, y: 250 + i * 190, width: 507,
        row_height: 26, cell_padding: 6, z: 2, table_bg: "",
        header: { font_family: "serif", size: 10, bold: true, color: "#ffffff", bg: "#1e3a8a", align: "center" },
        cell: { font_family: "serif", size: 10, bold: false, color: "#111827", align: "left", bg: "" },
        total_row: { bg: "#e8eef9" },
        borders: { color: "#334155", thickness: 0.6, style: "solid", outer: true, inner: true },
        columns: [
          { key: "subject", label: "SUBJECT", width: 240, visible: true, header_align: "left", cell_align: "left" },
          { key: "total", label: "TOTAL", width: 80, visible: true, header_align: "center", cell_align: "center" },
          { key: "min", label: "MIN. MARKS", width: 90, visible: true, header_align: "center", cell_align: "center" },
          { key: "obtained", label: "MARKS OBTAINED", width: 97, visible: true, header_align: "center", cell_align: "center" },
        ],
      },
    })),
    grand_total: {
      label: {
        visible: true, locked: false, text: "Grand Total :", font_family: "serif",
        size: 11, bold: true, italic: false, underline: false, color: "#111827",
        align: "right", x: 39, y: 588, width: 130, height: 22,
        bg_color: "", bg_opacity: 100, letter_spacing: 0, padding: 0,
        border_color: "#334155", border_thickness: 0, z: 2,
      },
      value: {
        visible: true, locked: false, text: "", font_family: "serif",
        size: 11, bold: true, italic: false, underline: false, color: "#111827",
        align: "left", x: 61, y: 588, width: 120, height: 22,
        bg_color: "", bg_opacity: 100, letter_spacing: 0, padding: 0,
        border_color: "#334155", border_thickness: 0, z: 2,
      },
    },
    extras: {
      percentage: { visible: true, locked: false, text: "Percentage : {value}%", font_family: "serif", size: 11, bold: true, italic: false, underline: false, color: "#111827", align: "left", x: 24.2, y: 608, width: 200, height: 18, bg_color: "", bg_opacity: 100, letter_spacing: 0, padding: 0, border_color: "#334155", border_thickness: 0, z: 2 },
      grade: { visible: true, locked: false, text: "Grade : '{value}'", font_family: "serif", size: 11, bold: true, italic: false, underline: false, color: "#111827", align: "left", x: 24.2, y: 626, width: 200, height: 18, bg_color: "", bg_opacity: 100, letter_spacing: 0, padding: 0, border_color: "#334155", border_thickness: 0, z: 2 },
      result: { visible: true, locked: false, text: "Result : {value}", font_family: "serif", size: 11, bold: true, italic: false, underline: false, color: "#166534", align: "right", x: 75.8, y: 608, width: 200, height: 18, bg_color: "", bg_opacity: 100, letter_spacing: 0, padding: 0, border_color: "#334155", border_thickness: 0, z: 2 },
      prepared_by: { visible: true, locked: false, text: "Prepared by", font_family: "serif", size: 9.5, bold: true, italic: false, underline: false, color: "#111827", align: "center", x: 16.3, y: 730, width: 110, height: 16, bg_color: "", bg_opacity: 100, letter_spacing: 0, padding: 0, border_color: "#334155", border_thickness: 0, show_line: true, z: 2 },
      signatory: { visible: true, locked: false, text: "Authorized Signatory", font_family: "serif", size: 9.5, bold: true, italic: false, underline: false, color: "#111827", align: "center", x: 78.2, y: 730, width: 140, height: 16, bg_color: "", bg_opacity: 100, letter_spacing: 0, padding: 0, border_color: "#334155", border_thickness: 0, show_line: true, z: 2 },
      qr: { visible: true, locked: false, x: 11.9, y: 754, size: 54, caption: "Scan to verify", show_caption: true, z: 2 },
      certificate_no: { visible: true, locked: false, text: "Certificate No. : {value}", font_family: "serif", size: 9.5, bold: false, italic: false, underline: false, color: "#4b5563", align: "left", x: 37, y: 701, width: 200, height: 16, bg_color: "", bg_opacity: 100, letter_spacing: 0, padding: 0, border_color: "#334155", border_thickness: 0, z: 2 },
      issue_date: { visible: true, locked: false, text: "Issue Date : {value}", font_family: "serif", size: 9.5, bold: false, italic: false, underline: false, color: "#4b5563", align: "left", x: 37, y: 687, width: 200, height: 16, bg_color: "", bg_opacity: 100, letter_spacing: 0, padding: 0, border_color: "#334155", border_thickness: 0, z: 2 },
      signature_img: { visible: true, locked: false, x: 78.2, y: 688, width: 120, height: 40, opacity: 100, rotation: 0, z: 2 },
      prepared_sign_img: { visible: true, locked: false, x: 16.3, y: 692, width: 100, height: 36, opacity: 100, rotation: 0, z: 2 },
      stamp_img: { visible: true, locked: false, x: 50, y: 668, width: 76, height: 76, opacity: 100, rotation: 0, z: 1 },
    },
    grade_scale: {
      visible: true, locked: false, x: 50, y: 660, width: 507, height: 30,
      font_family: "serif", size: 9.5, bold: false, color: "#111827",
      bg: "#f1f5f9", border_color: "#334155", border_thickness: 0.7,
      padding: 10, gap: 18, label: "Grade Scale :", z: 1,
      rows: [["A+", "Above 85%"], ["A", "75% - 84%"], ["B+", "65% - 74%"], ["B", "50% - 64%"], ["C", "Below 50%"]],
    },
  };
}

const SAMPLE_FIELD_TEXT = {
  f1: {
    enrollment_no: "Enrolment No. : LOV/225/045",
    registration_no: "Registration No. : 202600001",
    line_certify: "This is to Certified that",
    student_name: "Nirmal Kaur",
    father_name: "Father Name : Mahinder Singh",
    mother_name: "Mother Name : Kashmeer Kaur",
    line_completed: "Has successfully completed the course",
    course_name: "DIPLOMA IN COMPUTER APPLICATIONS",
    course_duration: "Duration : 1 Year",
    grade: "With 'A' Grade",
    session: "2025 – 2026",
    institute_name: "From the study Centre : Lovely Institute",
    certificate_no: "Certificate No. : LI-2026-0001",
    issue_date: "Issue Date : 06 Aug 2026",
  },
  f2: {
    enrollment_no: "Enrolment No. : LOV/225/045",
    registration_no: "Registration No. : 202600001",
    course_name: "Certificate Name : Diploma in Computer Applications",
    student_name: "Student's Name : Nirmal Kaur",
    father_name: "Father's Name : Mahinder Singh",
    session: "Session : 2025 – 2026",
    institute_name: "Name of Institute : Lovely Institute",
    course_duration: "Course Duration : 1 Year",
  },
};

function mergeBody(def, saved) {
  if (!saved) return def;
  const out = { ...def, ...saved };
  out.student_block = { ...def.student_block, ...(saved.student_block || {}) };
  if (saved.title_bar || def.title_bar) {
    out.title_bar = { ...(def.title_bar || {}), ...(saved.title_bar || {}) };
  }
  out.fields = { ...def.fields };
  for (const k of Object.keys(saved.fields || {})) {
    out.fields[k] = { ...(def.fields[k] || defaultField(50, 100)), ...saved.fields[k] };
  }
  if (saved.term_tables !== undefined || saved.marks_table) {
    if (saved.term_tables) {
      // merge saved term tables over defaults (by index)
      out.term_tables = saved.term_tables.map((tt, i) => {
        const dt = (def.term_tables || [])[Math.min(i, (def.term_tables || []).length - 1)] || {};
        return {
          ...dt, ...tt,
          title: { ...(dt.title || {}), ...(tt.title || {}) },
          table: {
            ...(dt.table || {}), ...(tt.table || {}),
            header: { ...(dt.table?.header || {}), ...(tt.table?.header || {}) },
            cell: { ...(dt.table?.cell || {}), ...(tt.table?.cell || {}) },
            borders: { ...(dt.table?.borders || {}), ...(tt.table?.borders || {}) },
            total_row: { ...(dt.table?.total_row || {}), ...(tt.table?.total_row || {}) },
            columns: (tt.table?.columns || dt.table?.columns || []).map((c0, j) => ({
              ...((dt.table?.columns || [])[j] || {}), ...c0,
            })),
          },
        };
      });
    } else if (saved.marks_table) {
      // migrate legacy single marks_table → two independent term tables
      const base = { ...defaultBodyF2().marks_table, ...saved.marks_table };
      out.term_tables = [1, 2].map((term, i) => ({
        id: `tt${term}`, term, visible: true, locked: false,
        title: { ...defaultBodyF2().term_tables[0].title, y: (base.y || 250) - 12 + i * 190 },
        table: { ...base, y: (base.y || 250) + i * 190 },
      }));
    }
  }
  if (saved.grand_total || def.grand_total) {
    const sg = saved.grand_total || {};
    const dg = def.grand_total || {};
    out.grand_total = {
      label: { ...(dg.label || {}), ...(sg.label || {}) },
      value: { ...(dg.value || {}), ...(sg.value || {}) },
    };
  }
  if (saved.extras || def.extras) {
    const se = saved.extras || {};
    const de = def.extras || {};
    out.extras = {};
    [...new Set([...Object.keys(de), ...Object.keys(se)])].forEach((k) => {
      out.extras[k] = { ...(de[k] || {}), ...(se[k] || {}) };
    });
  }
  if (saved.grade_scale || def.grade_scale) {
    out.grade_scale = { ...(def.grade_scale || {}), ...(saved.grade_scale || {}) };
  }
  if (saved.marks_table || def.marks_table) {
    const sm = saved.marks_table || {};
    const dm = def.marks_table || {};
    out.marks_table = { ...dm, ...sm };
    out.marks_table.header = { ...(dm.header || {}), ...(sm.header || {}) };
    out.marks_table.cell = { ...(dm.cell || {}), ...(sm.cell || {}) };
    out.marks_table.borders = { ...(dm.borders || {}), ...(sm.borders || {}) };
    out.marks_table.total_row = { ...(dm.total_row || {}), ...(sm.total_row || {}) };
    out.marks_table.columns = (sm.columns || dm.columns || []).map((c0, i) => ({
      ...((dm.columns || [])[i] || {}), ...c0,
    }));
  }
  return out;
}

function defaultElement(key) {
  const base = {
    visible: true, text: "", font_family: "serif", size: 10, bold: false,
    italic: false, underline: false, color: "#4b5563", align: "center",
    x: 50, y: 80, bg_color: "", bg_opacity: 100, letter_spacing: 0, padding: 0,
  };
  const presets = {
    institute_name: { size: 26, bold: true, color: "#1e3a8a", y: 45 },
    registration_info: { y: 78 },
    address: { y: 92 },
    contact: { y: 106 },
    email: { y: 120 },
  };
  return { ...base, ...(presets[key] || {}) };
}

function defaultLetterhead() {
  return {
    logos: [],
    elements: Object.fromEntries(
      Object.keys(ELEMENT_LABELS).map((k) => [k, defaultElement(k)])
    ),
    approval_lines: [],
    watermark: {
      type: "none", image: null, logo_id: "", logo_src: null, text: "",
      font_family: "serif", font_size: 80, color: "#1e3a8a",
      opacity: 12, rotation: 45, position: "center", x: null, y: null,
      width: 320, height: 320,
    },
  };
}

function normalizeTemplate(t) {
  const lh = { ...defaultLetterhead(), ...(t.letterhead || {}) };
  lh.elements = { ...defaultLetterhead().elements, ...(lh.elements || {}) };
  lh.watermark = { ...defaultLetterhead().watermark, ...(lh.watermark || {}) };
  const body = {
    f1: mergeBody(defaultBodyF1(), t.body?.f1),
    f2: mergeBody(defaultBodyF2(), t.body?.f2),
  };
  return { ...t, letterhead: lh, body };
}

const SAMPLE_F1 = {
  format: 1, student_name: "Nirmal Kaur", father_name: "Mahinder Singh",
  mother_name: "Kashmeer Kaur", course_name: "Diploma in Computer Applications",
  course_duration: "1 Year", grade: "A", session: "2025 – 2026",
  certificate_number: "LI-2026-0001", registration_number: "202600001",
  enrollment_number: "LOV/225/045", issue_date: "06 Aug 2026",
};
const SAMPLE_F2 = {
  ...SAMPLE_F1, format: 2,
  marks: [
    { subject: "COMPUTER FUNDAMENTALS", total: 100, min: 35, obtained: 88, term: 1 },
    { subject: "MICROSOFT OFFICE & SOFTWARE", total: 100, min: 35, obtained: 92, term: 1 },
    { subject: "OPERATING SYSTEMS & DTP", total: 100, min: 35, obtained: 79, term: 1 },
    { subject: "NETWORKING & TYPING", total: 100, min: 35, obtained: 81, term: 2 },
    { subject: "PRACTICLE 1", total: 100, min: 35, obtained: 85, term: 2 },
  ],
  total_marks: 500, marks_obtained: 425, percentage: 85, grade: "A+", result: "PASS",
};

export default function Designer() {
  const [searchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState(null);
  const [settings, setSettings] = useState(null);
  const [previewFormat, setPreviewFormat] = useState(1);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState("institute_name");
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: "" });
  const [saving, setSaving] = useState(false);
  const [fontLibOpen, setFontLibOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const [tRes, sRes] = await Promise.all([api.get("/templates"), api.get("/settings")]);
      setTemplates(tRes.data);
      setSettings(sRes.data);
      const want = searchParams.get("tpl");
      const pick =
        tRes.data.find((x) => x.id === want) ||
        tRes.data.find((x) => x.is_default) ||
        tRes.data[0];
      if (pick) setTpl(normalizeTemplate(pick));
    })();
    refreshCustomFonts();
  }, [searchParams]);

  const lh = tpl?.letterhead;

  const setLh = (fn) => setTpl((t) => ({ ...t, letterhead: fn(t.letterhead) }));
  const setEl = (key, patch) =>
    setLh((l) => ({ ...l, elements: { ...l.elements, [key]: { ...l.elements[key], ...patch } } }));
  const setLogo = (id, patch) =>
    setLh((l) => ({ ...l, logos: l.logos.map((g) => (g.id === id ? { ...g, ...patch } : g)) }));
  const setWm = (patch) =>
    setLh((l) => ({ ...l, watermark: { ...l.watermark, ...patch } }));
  const setApproval = (id, patch) =>
    setLh((l) => ({ ...l, approval_lines: l.approval_lines.map((a) => (a.id === id ? { ...a, ...patch } : a)) }));

  const bodyKey = previewFormat === 1 ? "f1" : "f2";
  const body = tpl?.body?.[bodyKey];
  const setBody = (fn) =>
    setTpl((t) => ({ ...t, body: { ...t.body, [bodyKey]: fn(t.body[bodyKey]) } }));
  const setField = (key, patch) =>
    setBody((b) => ({ ...b, fields: { ...b.fields, [key]: { ...b.fields[key], ...patch } } }));
  const setBlock = (patch) =>
    setBody((b) => ({ ...b, student_block: { ...b.student_block, ...patch } }));
  const setTable = (patch) =>
    setBody((b) => ({ ...b, marks_table: { ...b.marks_table, ...patch } }));
  const setTT = (id, patch) =>
    setBody((b) => ({ ...b, term_tables: b.term_tables.map((t) => (t.id === id ? { ...t, ...patch } : t)) }));
  const setTTTable = (id, patch) =>
    setBody((b) => ({ ...b, term_tables: b.term_tables.map((t) => (t.id === id ? { ...t, table: { ...t.table, ...patch } } : t)) }));
  const setTTTitle = (id, patch) =>
    setBody((b) => ({ ...b, term_tables: b.term_tables.map((t) => (t.id === id ? { ...t, title: { ...t.title, ...patch } } : t)) }));
  const setTTCol = (id, key, patch) =>
    setBody((b) => ({
      ...b,
      term_tables: b.term_tables.map((t) =>
        t.id === id
          ? { ...t, table: { ...t.table, columns: t.table.columns.map((c0) => (c0.key === key ? { ...c0, ...patch } : c0)) } }
          : t
      ),
    }));
  const moveTTCol = (id, i, dir) =>
    setBody((b) => ({
      ...b,
      term_tables: b.term_tables.map((t) => {
        if (t.id !== id) return t;
        const arr = [...t.table.columns];
        const j = i + dir;
        if (j < 0 || j >= arr.length) return t;
        [arr[i], arr[j]] = [arr[j], arr[i]];
        return { ...t, table: { ...t.table, columns: arr } };
      }),
    }));
  const addTermTable = () =>
    setBody((b) => {
      const defs = defaultBodyF2().term_tables[0];
      const last = b.term_tables[b.term_tables.length - 1];
      const baseY = last ? last.table.y + 190 : 250;
      const term = Math.max(0, ...b.term_tables.map((t) => t.term || 0)) + 1;
      return {
        ...b,
        term_tables: [...b.term_tables, {
          id: `tt-${Date.now()}`, term, visible: true, locked: false,
          title: { ...defs.title, y: baseY - 12 },
          table: { ...defs.table, y: baseY, columns: defs.table.columns.map((c0) => ({ ...c0 })) },
        }],
      };
    });
  const removeTermTable = (id) =>
    setBody((b) => ({ ...b, term_tables: b.term_tables.filter((t) => t.id !== id) }));
  const alignAllFields = (mode) =>
    setBody((b) => {
      const x = mode === "left" ? 8 : mode === "right" ? 92 : 50;
      const fields = {};
      for (const [k, v] of Object.entries(b.fields)) fields[k] = { ...v, x, align: mode };
      return { ...b, fields };
    });
  const setTitleBar = (patch) =>
    setBody((b) => ({ ...b, title_bar: { ...b.title_bar, ...patch } }));
  const setGT = (kind, patch) =>
    setBody((b) => ({ ...b, grand_total: { ...b.grand_total, [kind]: { ...b.grand_total[kind], ...patch } } }));
  const setExtra = (kind, patch) =>
    setBody((b) => ({ ...b, extras: { ...b.extras, [kind]: { ...(b.extras?.[kind] || {}), ...patch } } }));
  const setGradeScale = (patch) =>
    setBody((b) => ({ ...b, grade_scale: { ...b.grade_scale, ...patch } }));

  const elText = (el, key) => {
    if (el.text) return el.text;
    const s = settings || {};
    if (key === "institute_name") return (s.institute_name || "LOVELY INSTITUTE").toUpperCase();
    if (key === "registration_info") return s.registration_info || "Regd. under Societies Registration Act";
    if (key === "address") return s.address || "Institute address";
    if (key === "contact") return [s.phone ? `Ph: ${s.phone}` : "", s.website || ""].filter(Boolean).join("  |  ") || "Contact details";
    if (key === "email") return `Certificate Verification Email : ${s.verification_email || s.email || "verify@example.com"}`;
    return "";
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/templates/${tpl.id}`, tpl);
      setTpl(normalizeTemplate(data));
      const { data: list } = await api.get("/templates");
      setTemplates(list);
      toast.success(`Template saved (v${data.version || "?"})`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setDefault = async () => {
    await api.put(`/templates/${tpl.id}`, { ...tpl, is_default: true });
    toast.success("Set as default template — applies to both formats");
    const { data: list } = await api.get("/templates");
    setTemplates(list);
    setTpl((t) => ({ ...t, is_default: true }));
  };

  const newTemplate = async () => {
    const { data } = await api.post("/templates", {
      name: `Template ${templates.length + 1}`,
      heading: "CERTIFICATE",
      letterhead: defaultLetterhead(),
    });
    const { data: list } = await api.get("/templates");
    setTemplates(list);
    setTpl(normalizeTemplate(data));
    toast.success("New template created");
  };

  const pdfPreviewOpen = async () => {
    try {
      const student = previewFormat === 2
        ? { ...SAMPLE_F2, institute_name: settings?.institute_name }
        : { ...SAMPLE_F1, institute_name: settings?.institute_name };
      const { data } = await api.post(
        "/certificates/preview",
        { student, template: tpl },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      setPdfPreview({ open: true, url });
    } catch (e) {
      toast.error("PDF preview failed");
    }
  };

  if (!tpl || !settings) {
    return <div className="p-10 text-slate-400 text-sm">Loading designer…</div>;
  }

  return (
    <div className="flex flex-col h-screen">
      <Dialog open={fontLibOpen} onOpenChange={setFontLibOpen}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="font-library-dialog"
        >
          <DialogHeader>
            <DialogTitle>Font Library</DialogTitle>
          </DialogHeader>
          <FontLibrary />
        </DialogContent>
      </Dialog>
      {/* toolbar */}
      <div className="border-b border-slate-200 bg-white px-6 py-3 flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-lg font-bold text-slate-900">Certificate Template Designer</h1>
          <p className="text-xs text-slate-500">One common letterhead for Diploma & Marksheet</p>
        </div>
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          data-testid="open-font-library"
          onClick={() => setFontLibOpen(true)}
          className="gap-1.5"
        >
          <Type size={14} /> Fonts
        </Button>
        <Select
          value={tpl.id}
          onValueChange={(v) => {
            const t = templates.find((x) => x.id === v);
            if (t) setTpl(normalizeTemplate(t));
          }}
        >
          <SelectTrigger data-testid="designer-template-select" className="h-9 w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {templates.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}{t.is_default ? " ★" : ""} (v{t.version || 1})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          data-testid="designer-name"
          value={tpl.name}
          onChange={(e) => setTpl((t) => ({ ...t, name: e.target.value }))}
          className="h-9 w-48"
          placeholder="Template name"
        />
        <Button variant="outline" size="sm" data-testid="designer-new" onClick={newTemplate} className="h-9">
          <Plus size={14} className="mr-1" /> New
        </Button>
        <Button
          variant="outline" size="sm" data-testid="designer-set-default" onClick={setDefault}
          className="h-9" disabled={tpl.is_default}
        >
          <Star size={14} className="mr-1" /> {tpl.is_default ? "Default" : "Set as Default"}
        </Button>
        <Button variant="outline" size="sm" data-testid="designer-pdf-preview" onClick={pdfPreviewOpen} className="h-9">
          <FileText size={14} className="mr-1" /> PDF Preview
        </Button>
        <Button size="sm" data-testid="designer-save" onClick={save} disabled={saving}
          className="h-9 bg-blue-800 hover:bg-blue-900">
          {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
          Save Template
        </Button>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* controls */}
        <div className="w-[380px] shrink-0 border-r border-slate-200 bg-white overflow-y-auto p-4 space-y-4">
          {/* layers */}
          <Panel icon={<Layers size={15} />} title="Layers">
            <div className="space-y-0.5 max-h-64 overflow-y-auto">
              {[
                ...lh.logos.map((g, i) => [`logo:${g.id}`, `Logo ${i + 1}`]),
                ...Object.keys(ELEMENT_LABELS).map((k) => [`el:${k}`, ELEMENT_LABELS[k]]),
                ...lh.approval_lines.map((a, i) => [`ap:${a.id}`, `Approval line ${i + 1}`]),
                ...(previewFormat === 2 ? [["titlebar", "MARKS STATEMENT"]] : []),
                ["block", "Student Details"],
                ...(previewFormat === 1 ? F1_FIELD_ORDER : F2_FIELD_ORDER).map((k) => [`field:${k}`, FIELD_LABELS[k]]),
                ...(previewFormat === 2 && body?.term_tables
                  ? body.term_tables.flatMap((tt) => [
                      [`ttitle:${tt.id}`, `${tt.title?.text || `TERM ${tt.term}`} — title`],
                      [`ttable:${tt.id}`, `${tt.title?.text || `TERM ${tt.term}`} — table`],
                    ])
                  : []),
                ...(previewFormat === 2 && body?.grand_total
                  ? [["gt:label", "Grand Total — label"], ["gt:value", "Grand Total — value"]]
                  : []),
                ...(previewFormat === 2 && body?.extras
                  ? [
                      ["ex:percentage", "Percentage"],
                      ["ex:grade", "Grade"],
                      ["ex:result", "Result"],
                      ["ex:certificate_no", "Certificate No."],
                      ["ex:issue_date", "Issue Date"],
                      ["ex:prepared_by", "Prepared By"],
                      ["ex:signatory", "Authorized Signatory"],
                      ["ex:qr", "QR Code"],
                      ["ex:signature_img", "Authorized Signatory — image"],
                      ["ex:prepared_sign_img", "Prepared By — signature image"],
                      ["ex:stamp_img", "Stamp"],
                    ]
                  : []),
                ...(previewFormat === 2 && body?.grade_scale ? [["gscale", "Grade Scale"]] : []),
                ["watermark", "Watermark"],
              ].map(([id, label]) => (
                <button key={id} data-testid={`layer-${id.replace(/:/g, "-")}`}
                  onClick={() => setSelected(id)}
                  className={`w-full text-left px-2.5 py-1.5 rounded text-xs transition-soft ${
                    selected === id ? "bg-blue-800 text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </Panel>

          {/* logos */}
          <Panel icon={<ImageIcon size={15} />} title="Logos" badge={`${lh.logos.length}/4`}>
            {lh.logos.map((g, i) => (
              <div key={g.id} className="rounded-lg border border-slate-200 p-3 mb-2 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded border border-slate-200 bg-slate-50 overflow-hidden grid place-items-center shrink-0">
                    <img src={g.src} alt="" className="max-w-full max-h-full object-contain" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">Logo {i + 1}</span>
                  <div className="flex-1" />
                  <label className="text-xs text-blue-700 cursor-pointer hover:underline">
                    Replace
                    <input type="file" accept="image/*" className="hidden" data-testid={`logo-replace-${g.id}`}
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (f) setLogo(g.id, { src: await fileToDataUrl(f) });
                        e.target.value = "";
                      }} />
                  </label>
                  <button data-testid={`logo-visible-${g.id}`} onClick={() => setLogo(g.id, { visible: g.visible === false })}
                    className="p-1 text-slate-500 hover:text-slate-800" title="Show / hide">
                    {g.visible === false ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button data-testid={`logo-del-${g.id}`} onClick={() => setLh((l) => ({ ...l, logos: l.logos.filter((x) => x.id !== g.id) }))}
                    className="p-1 text-slate-500 hover:text-red-600" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <MiniField label="Position">
                    <Select value={g.position || "left"} onValueChange={(v) => setLogo(g.id, { position: v, x: null })}>
                      <SelectTrigger data-testid={`logo-pos-${g.id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="left">Left</SelectItem>
                        <SelectItem value="center">Center</SelectItem>
                        <SelectItem value="right">Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </MiniField>
                  <MiniField label="Width">
                    <Input type="number" data-testid={`logo-w-${g.id}`} value={g.width} className="h-8 text-xs"
                      onChange={(e) => setLogo(g.id, { width: Number(e.target.value) })} />
                  </MiniField>
                  <MiniField label="Height">
                    <Input type="number" data-testid={`logo-h-${g.id}`} value={g.height} className="h-8 text-xs"
                      onChange={(e) => setLogo(g.id, { height: Number(e.target.value) })} />
                  </MiniField>
                </div>
                <MiniField label={`Opacity — ${g.opacity ?? 100}%`}>
                  <input type="range" min="10" max="100" value={g.opacity ?? 100} data-testid={`logo-opacity-${g.id}`}
                    onChange={(e) => setLogo(g.id, { opacity: Number(e.target.value) })} className="w-full accent-blue-800" />
                </MiniField>
                <MiniField label="Spacing from top (pt)">
                  <Input type="number" value={g.y} className="h-8 text-xs" data-testid={`logo-spacing-${g.id}`}
                    onChange={(e) => setLogo(g.id, { y: Number(e.target.value) })} />
                </MiniField>
              </div>
            ))}
            {lh.logos.length < 4 && (
              <label data-testid="designer-add-logo"
                className="flex items-center justify-center gap-2 border border-dashed border-slate-300 rounded-lg py-2.5 text-sm text-blue-800 cursor-pointer hover:bg-blue-50 transition-soft">
                <Plus size={14} /> Upload logo
                <input type="file" accept="image/*" className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      const src = await fileToDataUrl(f);
                      setLh((l) => ({
                        ...l,
                        logos: [...l.logos, {
                          id: `lg-${Date.now()}`, src, visible: true,
                          position: ["left", "center", "right", "left"][l.logos.length] || "left",
                          x: null, y: 40, width: 70, height: 70, opacity: 100,
                        }],
                      }));
                    }
                    e.target.value = "";
                  }} />
              </label>
            )}
          </Panel>

          {/* institute text elements */}
          <Panel icon={<Type size={15} />} title="Institute Details">
            {Object.keys(ELEMENT_LABELS).map((key) => {
              const el = lh.elements[key];
              const open = expanded === key;
              return (
                <div key={key} className="rounded-lg border border-slate-200 mb-2">
                  <button
                    data-testid={`el-toggle-${key}`}
                    onClick={() => setExpanded(open ? null : key)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <span className="truncate">{ELEMENT_LABELS[key]}</span>
                    <span className="flex items-center gap-2">
                      <span
                        role="button"
                        data-testid={`el-visible-${key}`}
                        onClick={(e) => { e.stopPropagation(); setEl(key, { visible: !el.visible }); }}
                        className="text-slate-400 hover:text-slate-700"
                      >
                        {el.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
                      </span>
                      <span className="text-xs text-slate-400">{el.size}pt</span>
                    </span>
                  </button>
                  {open && (
                    <div className="px-3 pb-3 pt-1 border-t border-slate-100">
                      <TextControls
                        value={el}
                        onChange={(p) => setEl(key, p)}
                        textValue={el.text}
                        onTextChange={(v) => setEl(key, { text: v })}
                        textPlaceholder={`Default from Settings: ${elText({ ...el, text: "" }, key).slice(0, 40)}…`}
                        idPrefix={`el-${key}`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </Panel>

          {/* approvals */}
          <Panel icon={<Stamp size={15} />} title="Approvals / Recognition Lines"
            action={
              <button data-testid="approvals-add"
                onClick={() => setLh((l) => ({
                  ...l,
                  approval_lines: [...l.approval_lines, {
                    id: `ap-${Date.now()}`, text: "Approved / Recognized By: ",
                    font_family: "serif", size: 10, color: "#4b5563", bold: false,
                    italic: false, align: "center", x: 50, y: 134 + l.approval_lines.length * 13,
                    bg_color: "", bg_opacity: 100, letter_spacing: 0, visible: true,
                  }],
                }))}
                className="text-blue-800 hover:text-blue-900">
                <Plus size={15} />
              </button>
            }>
            {lh.approval_lines.length === 0 && (
              <p className="text-xs text-slate-400">No approval lines. e.g. "Under Sr. Act. 1860"</p>
            )}
            {lh.approval_lines.map((a, i) => (
              <div key={a.id} className="rounded-lg border border-slate-200 p-3 mb-2 space-y-2">
                <div className="flex items-center gap-1">
                  <Input value={a.text} data-testid={`ap-text-${a.id}`} className="h-8 text-xs"
                    onChange={(e) => setApproval(a.id, { text: e.target.value })} />
                  <button data-testid={`ap-up-${a.id}`} disabled={i === 0}
                    onClick={() => setLh((l) => {
                      const arr = [...l.approval_lines];
                      [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                      return { ...l, approval_lines: arr };
                    })}
                    className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp size={13} /></button>
                  <button data-testid={`ap-down-${a.id}`} disabled={i === lh.approval_lines.length - 1}
                    onClick={() => setLh((l) => {
                      const arr = [...l.approval_lines];
                      [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
                      return { ...l, approval_lines: arr };
                    })}
                    className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown size={13} /></button>
                  <button data-testid={`ap-del-${a.id}`}
                    onClick={() => setLh((l) => ({ ...l, approval_lines: l.approval_lines.filter((x) => x.id !== a.id) }))}
                    className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                </div>
                <TextControls value={a} onChange={(p) => setApproval(a.id, p)} idPrefix={`ap-${a.id}`} compact />
              </div>
            ))}
          </Panel>

          {/* watermark */}
          <Panel icon={<Droplets size={15} />} title="Watermark Settings">
            <div className="space-y-3">
              <div className="flex gap-1.5">
                {[["none", "None"], ["text", "Text"], ["image", "Image"], ["logo", "Logo"]].map(([v, lbl]) => (
                  <button key={v} type="button" data-testid={`wm-type-${v}`}
                    onClick={() => setWm({ type: v })}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-soft ${
                      lh.watermark.type === v ? "bg-blue-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}>
                    {lbl}
                  </button>
                ))}
              </div>

              {lh.watermark.type === "text" && (
                <>
                  <MiniField label="Watermark text">
                    <Input value={lh.watermark.text} data-testid="wm-text" className="h-8 text-xs"
                      placeholder={settings.institute_name}
                      onChange={(e) => setWm({ text: e.target.value })} />
                  </MiniField>
                  <MiniField label="Font">
                    <FontSelect value={lh.watermark.font_family} onChange={(v) => setWm({ font_family: v })} testId="wm-font" />
                  </MiniField>
                  <div className="grid grid-cols-2 gap-2">
                    <MiniField label="Font size">
                      <Input type="number" min={6} max={144} data-testid="wm-font-size" value={lh.watermark.font_size} className="h-8 text-xs"
                        onChange={(e) => setWm({ font_size: clampSize(e.target.value) })} />
                    </MiniField>
                    <MiniField label="Color">
                      <Input type="color" data-testid="wm-color" value={lh.watermark.color} className="h-8 w-full"
                        onChange={(e) => setWm({ color: e.target.value })} />
                    </MiniField>
                  </div>
                </>
              )}

              {lh.watermark.type === "image" && (
                <label data-testid="wm-upload"
                  className="flex items-center justify-center gap-2 border border-dashed border-slate-300 rounded-lg py-2.5 text-sm text-blue-800 cursor-pointer hover:bg-blue-50 transition-soft">
                  <Plus size={14} /> {lh.watermark.image ? "Replace watermark image" : "Upload watermark image"}
                  <input type="file" accept="image/*" className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (f) setWm({ image: await fileToDataUrl(f) });
                      e.target.value = "";
                    }} />
                </label>
              )}

              {lh.watermark.type === "logo" && (
                <MiniField label="Select logo">
                  <Select value={lh.watermark.logo_id || ""}
                    onValueChange={(v) => {
                      const lg = lh.logos.find((x) => x.id === v);
                      setWm({ logo_id: v, logo_src: lg?.src || null });
                    }}>
                    <SelectTrigger data-testid="wm-logo-select" className="h-8 text-xs"><SelectValue placeholder="Choose a logo" /></SelectTrigger>
                    <SelectContent>
                      {lh.logos.map((g, i) => (
                        <SelectItem key={g.id} value={g.id}>Logo {i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </MiniField>
              )}

              {lh.watermark.type !== "none" && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <MiniField label="Position">
                      <Select value={lh.watermark.position} onValueChange={(v) => setWm({ position: v, x: null, y: null })}>
                        <SelectTrigger data-testid="wm-position" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="center">Center</SelectItem>
                          <SelectItem value="top">Top</SelectItem>
                          <SelectItem value="bottom">Bottom</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </MiniField>
                    <MiniField label="Rotation">
                      <div className="flex gap-1">
                        {[0, 45, 90].map((r) => (
                          <button key={r} type="button" data-testid={`wm-rot-${r}`}
                            onClick={() => setWm({ rotation: r })}
                            className={`px-2 py-1 rounded text-xs ${lh.watermark.rotation === r ? "bg-blue-800 text-white" : "bg-slate-100"}`}>
                            {r}°
                          </button>
                        ))}
                        <Input type="number" data-testid="wm-rot-custom" value={lh.watermark.rotation} className="h-7 w-14 text-xs"
                          onChange={(e) => setWm({ rotation: Number(e.target.value) })} />
                      </div>
                    </MiniField>
                  </div>
                  {lh.watermark.type !== "text" && (
                    <div className="grid grid-cols-2 gap-2">
                      <MiniField label="Width">
                        <Input type="number" data-testid="wm-width" value={lh.watermark.width} className="h-8 text-xs"
                          onChange={(e) => setWm({ width: Number(e.target.value) })} />
                      </MiniField>
                      <MiniField label="Height">
                        <Input type="number" data-testid="wm-height" value={lh.watermark.height} className="h-8 text-xs"
                          onChange={(e) => setWm({ height: Number(e.target.value) })} />
                      </MiniField>
                    </div>
                  )}
                  <MiniField label={`Opacity — ${lh.watermark.opacity}% (recommended 10–20%)`}>
                    <input type="range" min="0" max="100" value={lh.watermark.opacity} data-testid="wm-opacity"
                      onChange={(e) => setWm({ opacity: Number(e.target.value) })} className="w-full accent-blue-800" />
                  </MiniField>
                </>
              )}
            </div>
          </Panel>

          {/* student details block + fields — same editing tool as header */}
          <Panel icon={<Users size={15} />} title="Student Details" badge="data-linked"
            action={
              <button data-testid="block-select" onClick={() => setSelected("block")}
                className="text-xs text-blue-800 hover:underline">
                Select block
              </button>
            }>
            {body && (
              <>
                <div className="flex items-center gap-1.5 mb-2">
                  <button data-testid="block-visible" onClick={() => setBlock({ visible: body.student_block.visible === false })}
                    className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Show / hide block">
                    {body.student_block.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                  <button data-testid="block-lock" onClick={() => setBlock({ locked: !body.student_block.locked })}
                    className={`p-1.5 rounded border transition-soft ${body.student_block.locked ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`} title="Lock / unlock">
                    {body.student_block.locked ? <Lock size={13} /> : <Unlock size={13} />}
                  </button>
                  <span className="w-px h-5 bg-slate-200 mx-0.5" />
                  <button data-testid="block-align-left" onClick={() => alignAllFields("left")}
                    className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Align block left"><AlignLeft size={13} /></button>
                  <button data-testid="block-align-center" onClick={() => alignAllFields("center")}
                    className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Align block center"><AlignCenter size={13} /></button>
                  <button data-testid="block-align-right" onClick={() => alignAllFields("right")}
                    className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Align block right"><AlignRight size={13} /></button>
                  <span className="w-px h-5 bg-slate-200 mx-0.5" />
                  <button data-testid="block-front" onClick={() => setBlock({ z: 10 })}
                    className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Bring to front"><ChevronsUp size={13} /></button>
                  <button data-testid="block-back" onClick={() => setBlock({ z: 0 })}
                    className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Send to back"><ChevronsDown size={13} /></button>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <MiniField label="X offset (%)">
                    <Input type="number" data-testid="block-dx" value={body.student_block.dx} className="h-8 text-xs"
                      onChange={(e) => setBlock({ dx: Number(e.target.value) })} />
                  </MiniField>
                  <MiniField label="Y offset (pt)">
                    <Input type="number" data-testid="block-dy" value={body.student_block.dy} className="h-8 text-xs"
                      onChange={(e) => setBlock({ dy: Number(e.target.value) })} />
                  </MiniField>
                  <MiniField label="Padding">
                    <Input type="number" data-testid="block-padding" value={body.student_block.padding} className="h-8 text-xs"
                      onChange={(e) => setBlock({ padding: Number(e.target.value) })} />
                  </MiniField>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <MiniField label="Width (pt)">
                    <Input type="number" data-testid="block-width" value={body.student_block.width} className="h-8 text-xs"
                      onChange={(e) => setBlock({ width: Number(e.target.value) })} />
                  </MiniField>
                  <MiniField label="Height (pt)">
                    <Input type="number" data-testid="block-height" value={body.student_block.height} className="h-8 text-xs"
                      onChange={(e) => setBlock({ height: Number(e.target.value) })} />
                  </MiniField>
                  <MiniField label="Margin">
                    <Input type="number" data-testid="block-margin" value={body.student_block.margin} className="h-8 text-xs"
                      onChange={(e) => setBlock({ margin: Number(e.target.value) })} />
                  </MiniField>
                </div>
                <div className="text-[11px] text-slate-400 mb-1.5">Individual fields (drag on canvas or edit below):</div>
                {(previewFormat === 1 ? F1_FIELD_ORDER : F2_FIELD_ORDER).map((key) => {
                  const el = body.fields[key] || defaultField(50, 100);
                  const open = expanded === `field:${key}`;
                  return (
                    <div key={key} className="rounded-lg border border-slate-200 mb-1.5">
                      <button data-testid={`field-toggle-${key}`} onClick={() => setExpanded(open ? null : `field:${key}`)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                        <span className="truncate">{FIELD_LABELS[key]}</span>
                        <span className="flex items-center gap-2">
                          <span role="button" data-testid={`field-visible-${key}`}
                            onClick={(e) => { e.stopPropagation(); setField(key, { visible: el.visible === false }); }}
                            className="text-slate-400 hover:text-slate-700">
                            {el.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
                          </span>
                          <span role="button" data-testid={`field-lock-${key}`}
                            onClick={(e) => { e.stopPropagation(); setField(key, { locked: !el.locked }); }}
                            className="text-slate-400 hover:text-slate-700">
                            {el.locked ? <Lock size={12} /> : <Unlock size={12} />}
                          </span>
                          <span className="text-xs text-slate-400">{el.size}pt</span>
                        </span>
                      </button>
                      {open && (
                        <div className="px-3 pb-3 pt-1 border-t border-slate-100">
                          <TextControls
                            value={el}
                            onChange={(p) => setField(key, p)}
                            textValue={el.text}
                            onTextChange={(v) => setField(key, { text: v })}
                            textPlaceholder={`{{${key}}} — empty = live student data`}
                            idPrefix={`field-${key}`}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </Panel>

          {/* marksheet title bar — editable static text element, same tool */}
          {previewFormat === 2 && body?.title_bar && (
            <Panel icon={<Type size={15} />} title="Marksheet Title (MARKS STATEMENT)" badge="static text"
              action={
                <button data-testid="tb-select" onClick={() => setSelected("titlebar")}
                  className="text-xs text-blue-800 hover:underline">
                  Select element
                </button>
              }>
              <div className="space-y-3">
                <div className="flex items-center gap-1.5">
                  <button data-testid="tb-visible" onClick={() => setTitleBar({ visible: body.title_bar.visible === false })}
                    className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Show / hide">
                    {body.title_bar.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                  <button data-testid="tb-lock" onClick={() => setTitleBar({ locked: !body.title_bar.locked })}
                    className={`p-1.5 rounded border transition-soft ${body.title_bar.locked ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`} title="Lock / unlock">
                    {body.title_bar.locked ? <Lock size={13} /> : <Unlock size={13} />}
                  </button>
                  <span className="w-px h-5 bg-slate-200 mx-0.5" />
                  <button data-testid="tb-front" onClick={() => setTitleBar({ z: 10 })}
                    className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Bring to front"><ChevronsUp size={13} /></button>
                  <button data-testid="tb-back" onClick={() => setTitleBar({ z: 0 })}
                    className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Send to back"><ChevronsDown size={13} /></button>
                </div>
                <MiniField label="Text (empty = default MARKS STATEMENT)">
                  <Input value={body.title_bar.text} data-testid="tb-text" className="h-8 text-xs"
                    placeholder="MARKS STATEMENT" onChange={(e) => setTitleBar({ text: e.target.value })} />
                </MiniField>
                <TextControls value={body.title_bar} onChange={setTitleBar} idPrefix="tb" />
                <div className="grid grid-cols-2 gap-2">
                  <MiniField label="Width (pt)">
                    <Input type="number" data-testid="tb-width" value={body.title_bar.width} className="h-8 text-xs"
                      onChange={(e) => setTitleBar({ width: Number(e.target.value) })} />
                  </MiniField>
                  <MiniField label="Height (pt)">
                    <Input type="number" data-testid="tb-height" value={body.title_bar.height} className="h-8 text-xs"
                      onChange={(e) => setTitleBar({ height: Number(e.target.value) })} />
                  </MiniField>
                </div>
              </div>
            </Panel>
          )}

          {/* term-wise marks tables — same editing tool, Format 2 only */}
          {previewFormat === 2 && body?.term_tables && (
            <Panel icon={<TableIcon size={15} />} title="Marks Tables (Term-wise)" badge="data-linked"
              action={
                <button data-testid="add-term-table" onClick={addTermTable}
                  className="text-blue-800 hover:text-blue-900" title="Add marks table">
                  <Plus size={15} />
                </button>
              }>
              <div className="space-y-2">
                {body.term_tables.map((tt) => {
                  const open = expanded === `tt:${tt.id}`;
                  const rowCount = tt.table.row_count ?? SAMPLE_F2.marks.filter((m) => (m.term ?? 1) === tt.term).length;
                  return (
                    <div key={tt.id} className="rounded-lg border border-slate-200">
                      <div className="flex items-center gap-1 px-2.5 py-2">
                        <button data-testid={`tt-toggle-${tt.id}`} onClick={() => setExpanded(open ? null : `tt:${tt.id}`)}
                          className="flex-1 text-left text-sm font-medium text-slate-700 hover:text-slate-900 truncate">
                          {tt.title?.text || `TERM ${tt.term}`} table
                        </button>
                        <button data-testid={`tt-select-${tt.id}`} onClick={() => setSelected(`ttable:${tt.id}`)}
                          className="text-[11px] text-blue-800 hover:underline px-1">Select</button>
                        <button data-testid={`tt-visible-${tt.id}`} onClick={() => setTT(tt.id, { visible: tt.visible === false })}
                          className="p-1 text-slate-400 hover:text-slate-700" title="Show / hide">
                          {tt.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                        <button data-testid={`tt-lock-${tt.id}`} onClick={() => setTT(tt.id, { locked: !tt.locked })}
                          className="p-1 text-slate-400 hover:text-slate-700" title="Lock / unlock">
                          {tt.locked ? <Lock size={12} /> : <Unlock size={12} />}
                        </button>
                        <button data-testid={`tt-del-${tt.id}`}
                          onClick={() => { if (confirm("Remove this term table from the template? Course subjects are not affected.")) removeTermTable(tt.id); }}
                          className="p-1 text-slate-400 hover:text-red-600" title="Delete table">
                          <Trash2 size={13} />
                        </button>
                      </div>
                      {open && (
                        <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-3">
                          <MiniField label="Term / group (subjects come from course configuration)">
                            <Select value={String(tt.term)} onValueChange={(v) => setTT(tt.id, { term: Number(v) })}>
                              <SelectTrigger data-testid={`tt-term-${tt.id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">Term 1</SelectItem>
                                <SelectItem value="2">Term 2</SelectItem>
                                <SelectItem value="3">Term 3</SelectItem>
                                <SelectItem value="4">Term 4 / Practical</SelectItem>
                              </SelectContent>
                            </Select>
                          </MiniField>
                          <div className="rounded-lg border border-slate-200 p-2.5">
                            <div className="text-[11px] font-medium text-slate-500 mb-1.5">Term title (same text editor)</div>
                            <TextControls value={tt.title} onChange={(p) => setTTTitle(tt.id, p)}
                              textValue={tt.title.text} onTextChange={(v) => setTTTitle(tt.id, { text: v })}
                              textPlaceholder={`TERM ${tt.term}`} idPrefix={`ttt-${tt.id}`} />
                          </div>
                          <TableControls t={tt.table} idPrefix={`tt-${tt.id}`}
                            onChange={(p) => setTTTable(tt.id, p)}
                            onCol={(key, p) => setTTCol(tt.id, key, p)}
                            onMoveCol={(i, dir) => moveTTCol(tt.id, i, dir)} />
                          <div className="rounded-lg border border-slate-200 p-2.5">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-[11px] font-medium text-slate-500">
                                Subject rows ({rowCount}) — structure only; subjects & marks stay dynamic
                              </span>
                              <button data-testid={`tt-row-add-${tt.id}`} onClick={() => setTTTable(tt.id, { row_count: rowCount + 1 })}
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-800 hover:underline">
                                <Plus size={11} /> Add Row
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {Array.from({ length: rowCount }).map((_, ri) => (
                                <span key={ri} data-testid={`tt-row-${tt.id}-${ri}`}
                                  className="inline-flex items-center gap-1 text-[10px] bg-slate-100 rounded px-1.5 py-0.5 text-slate-600">
                                  Row {ri + 1}
                                  <button data-testid={`tt-row-del-${tt.id}-${ri}`} disabled={rowCount <= 1}
                                    onClick={() => setTTTable(tt.id, { row_count: rowCount - 1 })}
                                    className="text-slate-400 hover:text-red-600 disabled:opacity-30" title="Delete row">
                                    <Trash2 size={10} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {body.term_tables.length === 0 && (
                  <p className="text-xs text-slate-400">No marks tables. Click + to add one.</p>
                )}
              </div>
            </Panel>
          )}

          {/* grand total — independent editable elements (Format 2) */}
          {previewFormat === 2 && body?.grand_total && (
            <Panel icon={<Type size={15} />} title="Grand Total" badge="data-linked">
              <p className="text-[11px] text-slate-400 mb-2">
                Independent of the marks tables — drag on canvas or fine-tune below. The value
                stays calculated automatically from the student's marks.
              </p>
              {["label", "value"].map((kind) => {
                const g = body.grand_total[kind] || {};
                const open = expanded === `gt:${kind}`;
                return (
                  <div key={kind} className="rounded-lg border border-slate-200 mb-1.5">
                    <div className="flex items-center gap-1 px-2.5 py-2">
                      <button data-testid={`gt-toggle-${kind}`} onClick={() => setExpanded(open ? null : `gt:${kind}`)}
                        className="flex-1 text-left text-sm font-medium text-slate-700 hover:text-slate-900 truncate">
                        {kind === "label" ? "Grand Total — Label" : "Grand Total — Value (dynamic)"}
                      </button>
                      <button data-testid={`gt-select-${kind}`} onClick={() => setSelected(`gt:${kind}`)}
                        className="text-[11px] text-blue-800 hover:underline px-1">Select</button>
                      <button data-testid={`gt-visible-${kind}`} onClick={() => setGT(kind, { visible: g.visible === false })}
                        className="p-1 text-slate-400 hover:text-slate-700" title="Show / hide">
                        {g.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button data-testid={`gt-lock-${kind}`} onClick={() => setGT(kind, { locked: !g.locked })}
                        className="p-1 text-slate-400 hover:text-slate-700" title="Lock / unlock">
                        {g.locked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                      <span className="text-xs text-slate-400 px-1">{g.size}pt</span>
                    </div>
                    {open && (
                      <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-3">
                        {kind === "label" ? (
                          <MiniField label="Label text">
                            <Input value={g.text ?? ""} data-testid="gt-label-text" className="h-8 text-xs"
                              placeholder="Grand Total :" onChange={(e) => setGT("label", { text: e.target.value })} />
                          </MiniField>
                        ) : (
                          <p className="text-[11px] text-slate-400">
                            Shows the live calculated total, e.g. <b>425 / 500</b>. Only position and styling are editable here.
                          </p>
                        )}
                        <TextControls value={{ ...g }} onChange={(p) => setGT(kind, p)} idPrefix={`gt-${kind}`} />
                        <div className="grid grid-cols-2 gap-2">
                          <MiniField label="Width (pt)">
                            <Input type="number" data-testid={`gt-${kind}-width`} value={g.width} className="h-8 text-xs"
                              onChange={(e) => setGT(kind, { width: Number(e.target.value) })} />
                          </MiniField>
                          <MiniField label="Height (pt)">
                            <Input type="number" data-testid={`gt-${kind}-height`} value={g.height} className="h-8 text-xs"
                              onChange={(e) => setGT(kind, { height: Number(e.target.value) })} />
                          </MiniField>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <MiniField label="Border color">
                            <Input type="color" data-testid={`gt-${kind}-border-color`} value={g.border_color || "#334155"} className="h-8 w-full"
                              onChange={(e) => setGT(kind, { border_color: e.target.value })} />
                          </MiniField>
                          <MiniField label="Border thickness (0 = none)">
                            <Input type="number" step="0.1" min="0" data-testid={`gt-${kind}-border-thickness`} value={g.border_thickness || 0} className="h-8 text-xs"
                              onChange={(e) => setGT(kind, { border_thickness: Number(e.target.value) })} />
                          </MiniField>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button data-testid={`gt-${kind}-front`} onClick={() => setGT(kind, { z: 10 })}
                            className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Bring to front"><ChevronsUp size={13} /></button>
                          <button data-testid={`gt-${kind}-back`} onClick={() => setGT(kind, { z: 0 })}
                            className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Send to back"><ChevronsDown size={13} /></button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </Panel>
          )}

          {/* Format 2 extras — result block, footer labels, QR (same editing tool) */}
          {previewFormat === 2 && body?.extras && (
            <Panel icon={<Type size={15} />} title="Result, Footer & QR" badge="data-linked">
              <p className="text-[11px] text-slate-400 mb-2">
                Independent elements — drag on canvas or fine-tune below. {"{value}"} in the text is replaced by the live calculated value.
              </p>
              {[
                ["percentage", "Percentage", true],
                ["grade", "Grade", true],
                ["result", "Result", true],
                ["certificate_no", "Certificate No.", true],
                ["issue_date", "Issue Date", true],
                ["prepared_by", "Prepared By", false],
                ["signatory", "Authorized Signatory", false],
              ].map(([kind, label, dynamic]) => {
                const g = body.extras[kind] || {};
                const open = expanded === `ex:${kind}`;
                return (
                  <div key={kind} className="rounded-lg border border-slate-200 mb-1.5">
                    <div className="flex items-center gap-1 px-2.5 py-2">
                      <button data-testid={`ex-toggle-${kind}`} onClick={() => setExpanded(open ? null : `ex:${kind}`)}
                        className="flex-1 text-left text-sm font-medium text-slate-700 hover:text-slate-900 truncate">{label}</button>
                      <button data-testid={`ex-select-${kind}`} onClick={() => setSelected(`ex:${kind}`)}
                        className="text-[11px] text-blue-800 hover:underline px-1">Select</button>
                      <button data-testid={`ex-visible-${kind}`} onClick={() => setExtra(kind, { visible: g.visible === false })}
                        className="p-1 text-slate-400 hover:text-slate-700" title="Show / hide">
                        {g.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button data-testid={`ex-lock-${kind}`} onClick={() => setExtra(kind, { locked: !g.locked })}
                        className="p-1 text-slate-400 hover:text-slate-700" title="Lock / unlock">
                        {g.locked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                    </div>
                    {open && (
                      <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-3">
                        <MiniField label={dynamic ? "Text ({value} = calculated value)" : "Text"}>
                          <Input value={g.text ?? ""} data-testid={`ex-${kind}-text`} className="h-8 text-xs"
                            placeholder={EX_DEFAULTS[kind]}
                            onChange={(e) => setExtra(kind, { text: e.target.value })} />
                        </MiniField>
                        {(kind === "prepared_by" || kind === "signatory") && (
                          <label className="flex items-center gap-2 text-xs text-slate-600">
                            <input type="checkbox" data-testid={`ex-${kind}-line`} checked={g.show_line !== false}
                              onChange={(e) => setExtra(kind, { show_line: e.target.checked })} className="w-3.5 h-3.5 accent-blue-800" />
                            Show signature rule line{kind === "signatory" ? " (signature image appears above it)" : ""}
                          </label>
                        )}
                        <TextControls value={{ ...g }} onChange={(p) => setExtra(kind, p)} idPrefix={`ex-${kind}`} />
                        <div className="grid grid-cols-2 gap-2">
                          <MiniField label="Width (pt)">
                            <Input type="number" data-testid={`ex-${kind}-width`} value={g.width} className="h-8 text-xs"
                              onChange={(e) => setExtra(kind, { width: Number(e.target.value) })} />
                          </MiniField>
                          <MiniField label="Height (pt)">
                            <Input type="number" data-testid={`ex-${kind}-height`} value={g.height} className="h-8 text-xs"
                              onChange={(e) => setExtra(kind, { height: Number(e.target.value) })} />
                          </MiniField>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <MiniField label="Border color">
                            <Input type="color" data-testid={`ex-${kind}-border-color`} value={g.border_color || "#334155"} className="h-8 w-full"
                              onChange={(e) => setExtra(kind, { border_color: e.target.value })} />
                          </MiniField>
                          <MiniField label="Border thickness (0 = none)">
                            <Input type="number" step="0.1" min="0" data-testid={`ex-${kind}-border-thickness`} value={g.border_thickness || 0} className="h-8 text-xs"
                              onChange={(e) => setExtra(kind, { border_thickness: Number(e.target.value) })} />
                          </MiniField>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button data-testid={`ex-${kind}-front`} onClick={() => setExtra(kind, { z: 10 })}
                            className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Bring to front"><ChevronsUp size={13} /></button>
                          <button data-testid={`ex-${kind}-back`} onClick={() => setExtra(kind, { z: 0 })}
                            className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Send to back"><ChevronsDown size={13} /></button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {(() => {
                const q = body.extras.qr || {};
                const open = expanded === "ex:qr";
                return (
                  <div className="rounded-lg border border-slate-200 mb-1.5">
                    <div className="flex items-center gap-1 px-2.5 py-2">
                      <button data-testid="ex-toggle-qr" onClick={() => setExpanded(open ? null : "ex:qr")}
                        className="flex-1 text-left text-sm font-medium text-slate-700 hover:text-slate-900">QR Code</button>
                      <button data-testid="ex-select-qr" onClick={() => setSelected("ex:qr")}
                        className="text-[11px] text-blue-800 hover:underline px-1">Select</button>
                      <button data-testid="ex-visible-qr" onClick={() => setExtra("qr", { visible: q.visible === false })}
                        className="p-1 text-slate-400 hover:text-slate-700" title="Show / hide">
                        {q.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button data-testid="ex-lock-qr" onClick={() => setExtra("qr", { locked: !q.locked })}
                        className="p-1 text-slate-400 hover:text-slate-700" title="Lock / unlock">
                        {q.locked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                    </div>
                    {open && (
                      <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-3">
                        <p className="text-[11px] text-slate-400">Encodes the live verification URL — stays scannable. Drag on canvas to reposition.</p>
                        <div className="grid grid-cols-3 gap-2">
                          <MiniField label="X center (%)"><Input type="number" data-testid="ex-qr-x" value={q.x} className="h-8 text-xs" onChange={(e) => setExtra("qr", { x: Number(e.target.value) })} /></MiniField>
                          <MiniField label="Y top (pt)"><Input type="number" data-testid="ex-qr-y" value={q.y} className="h-8 text-xs" onChange={(e) => setExtra("qr", { y: Number(e.target.value) })} /></MiniField>
                          <MiniField label="Size (pt)"><Input type="number" data-testid="ex-qr-size" value={q.size} className="h-8 text-xs" onChange={(e) => setExtra("qr", { size: Number(e.target.value) })} /></MiniField>
                        </div>
                        <MiniField label="Caption">
                          <Input value={q.caption ?? ""} data-testid="ex-qr-caption" className="h-8 text-xs" placeholder="Scan to verify" onChange={(e) => setExtra("qr", { caption: e.target.value })} />
                        </MiniField>
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          <input type="checkbox" data-testid="ex-qr-show-caption" checked={q.show_caption !== false}
                            onChange={(e) => setExtra("qr", { show_caption: e.target.checked })} className="w-3.5 h-3.5 accent-blue-800" />
                          Show caption
                        </label>
                        <div className="flex items-center gap-1.5">
                          <button data-testid="ex-qr-front" onClick={() => setExtra("qr", { z: 10 })} className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Bring to front"><ChevronsUp size={13} /></button>
                          <button data-testid="ex-qr-back" onClick={() => setExtra("qr", { z: 0 })} className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Send to back"><ChevronsDown size={13} /></button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* signature & stamp image elements (assets managed in Templates) */}
              <p className="text-[11px] font-medium text-slate-500 mt-3 mb-1">
                Signature &amp; stamp images — master assets are managed in Templates (shared with Format 1). Hiding here only removes them from this layout.
              </p>
              {[
                ["signature_img", "Authorized Signatory — image"],
                ["prepared_sign_img", "Prepared By — signature image"],
                ["stamp_img", "Stamp — image"],
              ].map(([kind, label]) => {
                const g = body.extras[kind] || {};
                const open = expanded === `ex:${kind}`;
                return (
                  <div key={kind} className="rounded-lg border border-slate-200 mb-1.5">
                    <div className="flex items-center gap-1 px-2.5 py-2">
                      <button data-testid={`ex-toggle-${kind}`} onClick={() => setExpanded(open ? null : `ex:${kind}`)}
                        className="flex-1 text-left text-sm font-medium text-slate-700 hover:text-slate-900 truncate">{label}</button>
                      <button data-testid={`ex-select-${kind}`} onClick={() => setSelected(`ex:${kind}`)}
                        className="text-[11px] text-blue-800 hover:underline px-1">Select</button>
                      <button data-testid={`ex-visible-${kind}`} onClick={() => setExtra(kind, { visible: g.visible === false })}
                        className="p-1 text-slate-400 hover:text-slate-700" title="Show / hide (layout only — asset kept)">
                        {g.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button data-testid={`ex-lock-${kind}`} onClick={() => setExtra(kind, { locked: !g.locked })}
                        className="p-1 text-slate-400 hover:text-slate-700" title="Lock / unlock">
                        {g.locked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                    </div>
                    {open && (
                      <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <MiniField label="X center (%)"><Input type="number" data-testid={`ex-${kind}-x`} value={g.x} className="h-8 text-xs" onChange={(e) => setExtra(kind, { x: Number(e.target.value) })} /></MiniField>
                          <MiniField label="Y top (pt)"><Input type="number" data-testid={`ex-${kind}-y`} value={g.y} className="h-8 text-xs" onChange={(e) => setExtra(kind, { y: Number(e.target.value) })} /></MiniField>
                          <MiniField label="Width (pt)"><Input type="number" data-testid={`ex-${kind}-width`} value={g.width} className="h-8 text-xs" onChange={(e) => setExtra(kind, { width: Number(e.target.value) })} /></MiniField>
                          <MiniField label="Height (pt)"><Input type="number" data-testid={`ex-${kind}-height`} value={g.height} className="h-8 text-xs" onChange={(e) => setExtra(kind, { height: Number(e.target.value) })} /></MiniField>
                          <MiniField label="Opacity (%)"><Input type="number" min="0" max="100" data-testid={`ex-${kind}-opacity`} value={g.opacity ?? 100} className="h-8 text-xs" onChange={(e) => setExtra(kind, { opacity: Number(e.target.value) })} /></MiniField>
                          <MiniField label="Rotation (°)"><Input type="number" step="1" data-testid={`ex-${kind}-rotation`} value={g.rotation ?? 0} className="h-8 text-xs" onChange={(e) => setExtra(kind, { rotation: Number(e.target.value) })} /></MiniField>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button data-testid={`ex-${kind}-front`} onClick={() => setExtra(kind, { z: 10 })}
                            className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Bring to front"><ChevronsUp size={13} /></button>
                          <button data-testid={`ex-${kind}-back`} onClick={() => setExtra(kind, { z: 0 })}
                            className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Send to back"><ChevronsDown size={13} /></button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </Panel>
          )}

          {/* Grade scale — independent element — Format 2 */}
          {previewFormat === 2 && body?.grade_scale && (
            <Panel icon={<TableIcon size={15} />} title="Grade Scale" badge="static"
              action={<button data-testid="gs-select" onClick={() => setSelected("gscale")} className="text-xs text-blue-800 hover:underline">Select element</button>}>
              {(() => {
                const gs = body.grade_scale;
                return (
                  <div className="space-y-3">
                    <div className="flex items-center gap-1.5">
                      <button data-testid="gs-visible" onClick={() => setGradeScale({ visible: gs.visible === false })}
                        className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Show / hide">
                        {gs.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button data-testid="gs-lock" onClick={() => setGradeScale({ locked: !gs.locked })}
                        className={`p-1.5 rounded border transition-soft ${gs.locked ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`} title="Lock / unlock">
                        {gs.locked ? <Lock size={13} /> : <Unlock size={13} />}
                      </button>
                      <span className="w-px h-5 bg-slate-200 mx-0.5" />
                      <button data-testid="gs-front" onClick={() => setGradeScale({ z: 10 })}
                        className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Bring to front"><ChevronsUp size={13} /></button>
                      <button data-testid="gs-back" onClick={() => setGradeScale({ z: 0 })}
                        className="p-1.5 rounded border border-slate-200 bg-white text-slate-600 hover:border-blue-300" title="Send to back"><ChevronsDown size={13} /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <MiniField label="X center (%)"><Input type="number" data-testid="gs-x" value={gs.x} className="h-8 text-xs" onChange={(e) => setGradeScale({ x: Number(e.target.value) })} /></MiniField>
                      <MiniField label="Y top (pt)"><Input type="number" data-testid="gs-y" value={gs.y} className="h-8 text-xs" onChange={(e) => setGradeScale({ y: Number(e.target.value) })} /></MiniField>
                      <MiniField label="Width (pt)"><Input type="number" data-testid="gs-width" value={gs.width} className="h-8 text-xs" onChange={(e) => setGradeScale({ width: Number(e.target.value) })} /></MiniField>
                      <MiniField label="Height (pt)"><Input type="number" data-testid="gs-height" value={gs.height} className="h-8 text-xs" onChange={(e) => setGradeScale({ height: Number(e.target.value) })} /></MiniField>
                      <MiniField label="Font size"><Input type="number" data-testid="gs-size" value={gs.size} className="h-8 text-xs" onChange={(e) => setGradeScale({ size: clampSize(e.target.value) })} /></MiniField>
                      <MiniField label="Padding (pt)"><Input type="number" data-testid="gs-padding" value={gs.padding} className="h-8 text-xs" onChange={(e) => setGradeScale({ padding: Number(e.target.value) })} /></MiniField>
                      <MiniField label="Text color"><Input type="color" data-testid="gs-color" value={gs.color || "#111827"} className="h-8 w-full" onChange={(e) => setGradeScale({ color: e.target.value })} /></MiniField>
                      <MiniField label="Background"><Input type="color" data-testid="gs-bg" value={gs.bg || "#f1f5f9"} className="h-8 w-full" onChange={(e) => setGradeScale({ bg: e.target.value })} /></MiniField>
                      <MiniField label="Border color"><Input type="color" data-testid="gs-border-color" value={gs.border_color || "#334155"} className="h-8 w-full" onChange={(e) => setGradeScale({ border_color: e.target.value })} /></MiniField>
                      <MiniField label="Border thickness"><Input type="number" step="0.1" min="0" data-testid="gs-border-thickness" value={gs.border_thickness ?? 0.7} className="h-8 text-xs" onChange={(e) => setGradeScale({ border_thickness: Number(e.target.value) })} /></MiniField>
                    </div>
                    <MiniField label="Font family">
                      <FontSelect value={gs.font_family} onChange={(v) => setGradeScale({ font_family: v })} testId="gs-font" />
                    </MiniField>
                    <MiniField label="Label">
                      <Input value={gs.label ?? ""} data-testid="gs-label" className="h-8 text-xs" placeholder="Grade Scale :" onChange={(e) => setGradeScale({ label: e.target.value })} />
                    </MiniField>
                    <MiniField label="Scale rows (one per line: Grade = Range)">
                      <textarea data-testid="gs-rows" className="w-full text-xs border border-slate-200 rounded-md p-2 h-20"
                        value={(gs.rows || []).map((r) => `${r[0]} = ${r[1]}`).join("\n")}
                        onChange={(e) =>
                          setGradeScale({
                            rows: e.target.value.split("\n").map((l) => {
                              const [g0, ...rest] = l.split("=");
                              return [(g0 || "").trim(), rest.join("=").trim()];
                            }).filter((r) => r[0]),
                          })
                        } />
                    </MiniField>
                  </div>
                );
              })()}
            </Panel>
          )}

          <p className="text-[11px] text-slate-400 px-1 pb-4">
            Tip: click any element in the preview to select it, then drag to reposition. Numbers on the left fine-tune position (X = % of page width, Y = pt from top).
          </p>
        </div>

        {/* preview */}
        <div className="flex-1 min-w-0 bg-slate-100 overflow-auto p-6">
          <div className="flex items-center gap-2 mb-4">
            {[[1, "Format 1 Preview — Diploma"], [2, "Format 2 Preview — Marksheet"]].map(([v, lbl]) => (
              <button key={v} data-testid={`designer-tab-f${v}`} onClick={() => setPreviewFormat(v)}
                className={`px-4 h-9 rounded-md text-sm font-medium transition-soft ${
                  previewFormat === v ? "bg-blue-800 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-blue-300"
                }`}>
                {lbl}
              </button>
            ))}
          </div>
          <PreviewPane
            tpl={tpl}
            format={previewFormat}
            elText={elText}
            selected={selected}
            onSelect={setSelected}
            onMoveEl={(key, xPct, y) => setEl(key, { x: xPct, y })}
            onMoveLogo={(id, xPct, y) => setLogo(id, { x: xPct, y })}
            onMoveApproval={(id, xPct, y) => setApproval(id, { x: xPct, y })}
            onMoveWm={(xPct, y) => setWm({ position: "custom", x: xPct, y })}
            onMoveField={(key, xPct, y) => setField(key, { x: xPct, y })}
            onMoveBlock={(dx, dy) => setBlock({ dx, dy })}
            onMoveTermTable={(id, xPct, y) => setTTTable(id, { x: xPct, y })}
            onMoveTermTitle={(id, xPct, y) => setTTTitle(id, { x: xPct, y })}
            onMoveTitleBar={(xPct, y) => setTitleBar({ x: xPct, y })}
            onMoveGT={(kind, xPct, y) => setGT(kind, { x: xPct, y })}
            onMoveExtra={(kind, xPct, y) => setExtra(kind, { x: xPct, y })}
            onMoveQR={(xPct, y) => setExtra("qr", { x: xPct, y })}
            onMoveGradeScale={(xPct, y) => setGradeScale({ x: xPct, y })}
            sigAssets={{
              signature: tpl?.signature || settings?.signature || "",
              prepared_signature: tpl?.prepared_signature || settings?.prepared_signature || "",
              stamp: tpl?.stamp || settings?.stamp || "",
            }}
          />
        </div>
      </div>

      <Dialog open={pdfPreview.open} onOpenChange={(v) => setPdfPreview({ open: v, url: pdfPreview.url })}>
        <DialogContent className="max-w-5xl w-[90vw] h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>PDF preview — {previewFormat === 2 ? "Format 2 Marksheet" : "Format 1 Diploma"}</DialogTitle>
          </DialogHeader>
          <iframe src={pdfPreview.url} title="pdf preview" className="w-full flex-1 rounded border border-slate-200" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- preview ---------------- */

function PreviewPane({ tpl, format, elText, selected, onSelect, onMoveEl, onMoveLogo, onMoveApproval, onMoveWm, onMoveField, onMoveBlock, onMoveTermTable, onMoveTermTitle, onMoveTitleBar, onMoveGT, onMoveExtra, onMoveQR, onMoveGradeScale, sigAssets }) {
  const [W, H] = format === 1 ? [842, 595] : [595, 842];
  const fmtKey = format === 1 ? "f1" : "f2";
  const body = tpl.body?.[fmtKey];
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.7);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setScale(Math.min(1, (el.clientWidth - 8) / W));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [W]);

  const lh = tpl.letterhead;
  const wm = lh.watermark;

  const wmPos = useMemo(() => {
    if (wm.x != null && wm.y != null) return { left: (wm.x / 100) * W, top: wm.y };
    if (wm.position === "top") return { left: W / 2, top: H * 0.22 };
    if (wm.position === "bottom") return { left: W / 2, top: H * 0.78 };
    return { left: W / 2, top: H / 2 };
  }, [wm, W, H]);

  const logoX = (g) => {
    if (g.x != null) return (g.x / 100) * W;
    const w = g.width || 70;
    if (g.position === "right") return W - 56 - w / 2;
    if (g.position === "center") return W / 2;
    return 56 + w / 2;
  };

  return (
    <div ref={wrapRef} className="w-full" onClick={() => onSelect(null)}>
      <div
        data-testid="preview-page"
        style={{ width: W, height: H, transform: `scale(${scale})`, transformOrigin: "top left" }}
        className="relative bg-white shadow-xl mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* border mock */}
        <div className="absolute inset-0 pointer-events-none" style={{ border: "3px solid #1e3a8a", margin: 16 }} />
        <div className="absolute inset-0 pointer-events-none" style={{ border: "1px solid #1e3a8a", margin: 24 }} />

        {/* watermark */}
        {wm.type !== "none" && (
          <Draggable id="watermark" x={wmPos.left} y={wmPos.top} scale={scale}
            selected={selected === "watermark"} onSelect={onSelect}
            onMove={(id, xPt, yPt) => onMoveWm(Math.round((xPt / W) * 1000) / 10, Math.round(yPt))}
            style={{ transform: "translate(-50%, -50%)" }}
          >
            <div
              data-testid="prev-wm"
              style={{
                opacity: (wm.opacity ?? 12) / 100,
                transform: `rotate(${wm.rotation || 0}deg)`,
                pointerEvents: "none",
              }}
            >
              {wm.type === "text" ? (
                <span style={{
                  fontFamily: fontCss(wm.font_family),
                  fontSize: wm.font_size, color: wm.color, fontWeight: 700, whiteSpace: "nowrap",
                }}>
                  {wm.text || "WATERMARK"}
                </span>
              ) : (
                (wm.type === "image" ? wm.image : wm.logo_src) && (
                  <img src={wm.type === "image" ? wm.image : wm.logo_src} alt=""
                    style={{ width: wm.width, height: wm.height, objectFit: "contain" }} />
                )
              )}
            </div>
          </Draggable>
        )}

        {/* logos */}
        {lh.logos.filter((g) => g.visible !== false && g.src).map((g) => (
          <Draggable key={g.id} id={`logo:${g.id}`} x={logoX(g)} y={(g.y ?? 40)} scale={scale}
            selected={selected === `logo:${g.id}`} onSelect={onSelect}
            onMove={(id, xPt, yPt) => onMoveLogo(g.id, Math.round((xPt / W) * 1000) / 10, Math.round(yPt))}
            style={{ transform: "translateX(-50%)", opacity: (g.opacity ?? 100) / 100 }}
          >
            <img src={g.src} alt="" data-testid={`prev-logo-${g.id}`}
              style={{ width: g.width, height: g.height, objectFit: "contain", pointerEvents: "none" }} />
          </Draggable>
        ))}

        {/* text elements */}
        {Object.keys(ELEMENT_LABELS).map((key) => {
          const el = lh.elements[key];
          if (el.visible === false) return null;
          const text = elText(el, key);
          if (!text) return null;
          return (
            <Draggable key={key} id={`el:${key}`} x={(el.x / 100) * W} y={el.y - el.size} scale={scale}
              selected={selected === `el:${key}`} onSelect={onSelect}
              onMove={(id, xPt, yPt) => onMoveEl(key, Math.round((xPt / W) * 1000) / 10, Math.round(yPt + el.size))}
              style={{
                transform: el.align === "left" ? "none" : el.align === "right" ? "translateX(-100%)" : "translateX(-50%)",
              }}
            >
              <span
                data-testid={`prev-el-${key}`}
                style={{
                  fontFamily: fontCss(el.font_family),
                  fontSize: el.size,
                  fontWeight: el.bold ? 700 : 400,
                  fontStyle: el.italic ? "italic" : "normal",
                  textDecoration: el.underline ? "underline" : "none",
                  color: el.color,
                  letterSpacing: el.letter_spacing || 0,
                  background: el.bg_color ? hexToRgba(el.bg_color, (el.bg_opacity ?? 100) / 100) : "transparent",
                  padding: el.padding || 0,
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                }}
              >
                {text}
              </span>
            </Draggable>
          );
        })}

        {/* approval lines */}
        {lh.approval_lines.filter((a) => a.visible !== false && a.text).map((a) => (
          <Draggable key={a.id} id={`ap:${a.id}`} x={(a.x / 100) * W} y={a.y - a.size} scale={scale}
            selected={selected === `ap:${a.id}`} onSelect={onSelect}
            onMove={(id, xPt, yPt) =>
              onMoveApproval(a.id, Math.round((xPt / W) * 1000) / 10, Math.round(yPt + a.size))
            }
            style={{
              transform: a.align === "left" ? "none" : a.align === "right" ? "translateX(-100%)" : "translateX(-50%)",
            }}
          >
            <span style={{
              fontFamily: fontCss(a.font_family),
              fontSize: a.size, fontWeight: a.bold ? 700 : 400,
              fontStyle: a.italic ? "italic" : "normal", color: a.color,
              letterSpacing: a.letter_spacing || 0,
              background: a.bg_color ? hexToRgba(a.bg_color, (a.bg_opacity ?? 100) / 100) : "transparent",
              whiteSpace: "nowrap", pointerEvents: "none",
            }}>
              {a.text}
            </span>
          </Draggable>
        ))}

        {/* student details fields (data-bound, editable) */}
        {body?.student_block?.visible !== false && Object.entries(body?.fields || {}).map(([key, el]) => {
          if (el.visible === false) return null;
          const text = el.text || SAMPLE_FIELD_TEXT[fmtKey][key];
          if (!text) return null;
          const blk = body.student_block;
          const fx = ((el.x + (blk.dx || 0)) / 100) * W;
          const fy = el.y + (blk.dy || 0) + (blk.padding || 0);
          return (
            <Draggable key={key} id={`field:${key}`} x={fx} y={fy - el.size} scale={scale}
              selected={selected === `field:${key}`} onSelect={onSelect}
              disabled={el.locked || blk.locked}
              onMove={(id, xPt, yPt) =>
                onMoveField(key,
                  Math.round((xPt / W) * 1000) / 10 - (blk.dx || 0),
                  Math.round(yPt + el.size) - (blk.dy || 0) - (blk.padding || 0))
              }
              style={{
                transform: el.align === "left" ? "none" : el.align === "right" ? "translateX(-100%)" : "translateX(-50%)",
                zIndex: blk.z || 1,
              }}
            >
              <span
                data-testid={`prev-field-${key}`}
                style={{
                  fontFamily: fontCss(el.font_family),
                  fontSize: el.size,
                  fontWeight: el.bold ? 700 : 400,
                  fontStyle: el.italic ? "italic" : "normal",
                  textDecoration: el.underline ? "underline" : "none",
                  color: el.color,
                  letterSpacing: el.letter_spacing || 0,
                  background: el.bg_color ? hexToRgba(el.bg_color, (el.bg_opacity ?? 100) / 100) : "transparent",
                  padding: el.padding || 0,
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                }}
              >
                {text}
              </span>
            </Draggable>
          );
        })}

        {/* student block outline / handle */}
        {body && (() => {
          const blk = body.student_block;
          const visFields = Object.values(body.fields || {}).filter((f) => f.visible !== false);
          if (!visFields.length) return null;
          const minY = Math.min(...visFields.map((f) => f.y));
          const maxY = Math.max(...visFields.map((f) => f.y + f.size));
          const bw = blk.width || 560;
          const bx = (W * 0.5) + ((blk.dx || 0) / 100) * W - bw / 2;
          const by = minY + (blk.dy || 0) - 24;
          const bh = Math.max(blk.height || 0, maxY - minY + 48);
          return (
            <Draggable id="block" x={bx} y={by} scale={scale}
              selected={selected === "block"} onSelect={onSelect}
              disabled={blk.locked}
              onMove={(id, xPt, yPt) =>
                onMoveBlock(
                  Math.round((((xPt + bw / 2) / W) * 100 - 50) * 10) / 10,
                  Math.round(yPt + 24 - minY)
                )
              }
              style={{ zIndex: (blk.z || 1) - 1 }}
            >
              <div
                data-testid="prev-block"
                style={{
                  width: bw, height: bh, pointerEvents: "none",
                  border: `1.5px dashed ${selected === "block" ? "#2563eb" : "rgba(37,99,235,0.25)"}`,
                  background: selected === "block" ? "rgba(37,99,235,0.05)" : "transparent",
                }}
              />
            </Draggable>
          );
        })()}

        {/* marksheet title bar (editable static element) */}
        {fmtKey === "f2" && body?.title_bar && body.title_bar.visible !== false && (() => {
          const tb = body.title_bar;
          const bw = Math.max(tb.width || 260, 40);
          const bh = tb.height || 26;
          const bx = (tb.x / 100) * W;
          const by = tb.y - bh * 0.72;
          return (
            <Draggable id="titlebar"
              x={tb.align === "left" ? bx : tb.align === "right" ? bx - bw : bx - bw / 2}
              y={by} scale={scale}
              selected={selected === "titlebar"} onSelect={onSelect}
              disabled={tb.locked}
              onMove={(id, xPt, yPt) => {
                const anchor = tb.align === "left" ? xPt : tb.align === "right" ? xPt + bw : xPt + bw / 2;
                onMoveTitleBar(Math.round((anchor / W) * 1000) / 10, Math.round(yPt + bh * 0.72));
              }}
              style={{ zIndex: tb.z || 2 }}
            >
              <div
                data-testid="prev-title-bar"
                style={{
                  width: bw, height: bh,
                  background: tb.bg_color ? hexToRgba(tb.bg_color, (tb.bg_opacity ?? 100) / 100) : "transparent",
                  borderRadius: tb.border_radius ?? 4,
                  display: "flex", alignItems: "center",
                  justifyContent: tb.align === "left" ? "flex-start" : tb.align === "right" ? "flex-end" : "center",
                  padding: tb.padding || 0,
                  pointerEvents: "none",
                }}
              >
                <span style={{
                  fontFamily: fontCss(tb.font_family),
                  fontSize: tb.size, fontWeight: tb.bold ? 700 : 400,
                  fontStyle: tb.italic ? "italic" : "normal",
                  textDecoration: tb.underline ? "underline" : "none",
                  color: tb.color, letterSpacing: tb.letter_spacing || 0,
                  whiteSpace: "nowrap",
                }}>
                  {tb.text || "MARKS STATEMENT"}
                </span>
              </div>
            </Draggable>
          );
        })()}

        {/* term-wise marks tables (data-bound, editable) — Format 2 */}
        {fmtKey === "f2" && body?.term_tables && body.term_tables.filter((tt) => tt.visible !== false).map((tt) => {
          const t = tt.table;
          const ttl = tt.title || {};
          const cols = t.columns.filter((c0) => c0.visible !== false);
          const totalW = cols.reduce((a, c0) => a + (c0.width || 100), 0) || 1;
          const x0 = (t.x / 100) * W - t.width / 2;
          const bCss = () => {
            const b = t.borders;
            const s = b.style === "dashed" ? "dashed" : b.style === "dotted" ? "dotted" : "solid";
            return `${b.thickness}px ${s} ${b.color}`;
          };
          const rows = (() => {
            const srows = SAMPLE_F2.marks.filter((m) => (m.term ?? 1) === tt.term);
            const rc = t.row_count ?? srows.length;
            return Array.from({ length: Math.max(rc, 1) }, (_, i) => srows[i] || { subject: "", total: "", min: "", obtained: "" });
          })();
          const subT = rows.reduce((a, m) => a + (Number(m.total) || 0), 0);
          const subO = rows.reduce((a, m) => a + (Number(m.obtained) || 0), 0);
          const tsize = ttl.size || 13;
          return (
            <React.Fragment key={tt.id}>
              {/* term title — same text editor */}
              {ttl.visible !== false && (
                <Draggable id={`ttitle:${tt.id}`} x={(ttl.x / 100) * W} y={(ttl.y ?? 240) - tsize} scale={scale}
                  selected={selected === `ttitle:${tt.id}`} onSelect={onSelect}
                  disabled={tt.locked || ttl.locked}
                  onMove={(id, xPt, yPt) =>
                    onMoveTermTitle(tt.id, Math.round((xPt / W) * 1000) / 10, Math.round(yPt + tsize))
                  }
                  style={{
                    transform: ttl.align === "left" ? "none" : ttl.align === "right" ? "translateX(-100%)" : "translateX(-50%)",
                    zIndex: ttl.z || 2,
                  }}
                >
                  <span
                    data-testid={`prev-ttitle-${tt.id}`}
                    style={{
                      fontFamily: fontCss(ttl.font_family),
                      fontSize: tsize, fontWeight: ttl.bold !== false ? 700 : 400,
                      fontStyle: ttl.italic ? "italic" : "normal",
                      textDecoration: ttl.underline ? "underline" : "none",
                      color: ttl.color || "#1e3a8a",
                      letterSpacing: ttl.letter_spacing || 0,
                      background: ttl.bg_color ? hexToRgba(ttl.bg_color, (ttl.bg_opacity ?? 100) / 100) : "transparent",
                      padding: ttl.padding || 0,
                      whiteSpace: "nowrap", pointerEvents: "none",
                    }}
                  >
                    {ttl.text || `TERM ${tt.term}`}
                  </span>
                </Draggable>
              )}

              {/* term table */}
              <Draggable id={`ttable:${tt.id}`} x={x0} y={t.y} scale={scale}
                selected={selected === `ttable:${tt.id}` || (selected || "").startsWith(`tcol:${tt.id}:`)}
                onSelect={onSelect}
                disabled={tt.locked}
                onMove={(id, xPt, yPt) =>
                  onMoveTermTable(tt.id, Math.round(((xPt + t.width / 2) / W) * 1000) / 10, Math.round(yPt))
                }
                style={{ zIndex: t.z || 2 }}
              >
                <table
                  data-testid={`prev-ttable-${tt.id}`}
                  style={{
                    width: t.width, borderCollapse: "collapse",
                    background: t.table_bg || "transparent",
                    outline: t.borders.outer !== false ? bCss() : "none",
                    pointerEvents: "none",
                  }}
                >
                  <thead>
                    <tr>
                      {cols.map((col) => (
                        <th key={col.key} data-testid={`prev-tcol-${tt.id}-${col.key}`}
                          style={{
                            width: ((col.width || 100) / totalW) * t.width,
                            background: t.header.bg, color: t.header.color,
                            fontFamily: fontCss(t.header.font_family),
                            fontSize: t.header.size, fontWeight: t.header.bold ? 700 : 400,
                            textAlign: col.header_align || t.header.align,
                            padding: t.cell_padding,
                            border: t.borders.inner !== false ? bCss() : "none",
                            outline: selected === `tcol:${tt.id}:${col.key}` ? "2px solid #f59e0b" : "none",
                            pointerEvents: "auto", cursor: "pointer",
                          }}
                          onClick={(e) => { e.stopPropagation(); onSelect(`tcol:${tt.id}:${col.key}`); }}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((m, i) => (
                      <tr key={`${m.subject || "row"}-${i}`}>
                        {cols.map((col) => (
                          <td key={col.key}
                            style={{
                              padding: t.cell_padding, height: t.row_height - 2 * t.cell_padding,
                              fontFamily: fontCss(t.cell.font_family),
                              fontSize: t.cell.size, color: t.cell.color,
                              fontWeight: t.cell.bold ? 700 : 400,
                              textAlign: col.cell_align || t.cell.align,
                              background: t.cell.bg || "transparent",
                              border: t.borders.inner !== false ? bCss() : "none",
                            }}>
                            {col.key === "subject" ? m.subject : col.key === "total" ? m.total : col.key === "min" ? m.min : m.obtained}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr>
                      {cols.map((col, i) => (
                        <td key={col.key}
                          style={{
                            padding: t.cell_padding, fontWeight: 700,
                            fontFamily: fontCss(t.cell.font_family),
                            fontSize: t.cell.size, background: t.total_row.bg,
                            textAlign: col.cell_align || t.cell.align,
                            border: t.borders.inner !== false ? bCss() : "none",
                          }}>
                          {i === 0 ? `TERM ${tt.term} TOTAL` : col.key === "total" ? subT : col.key === "obtained" ? subO : ""}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </Draggable>
            </React.Fragment>
          );
        })}

        {/* grand total — independent editable elements (label + dynamic value) — Format 2 */}
        {fmtKey === "f2" && body?.grand_total && ["label", "value"].map((kind) => {
          const g = body.grand_total[kind] || {};
          if (g.visible === false) return null;
          const w = g.width || 120;
          const h = g.height || 22;
          const gtext = kind === "label"
            ? (g.text || "Grand Total :")
            : `${SAMPLE_F2.marks_obtained} / ${SAMPLE_F2.total_marks}`;
          return (
            <Draggable key={kind} id={`gt:${kind}`} x={(g.x / 100) * W - w / 2} y={g.y ?? 588} scale={scale}
              selected={selected === `gt:${kind}`} onSelect={onSelect}
              disabled={g.locked}
              onMove={(id, xPt, yPt) =>
                onMoveGT(kind, Math.round(((xPt + w / 2) / W) * 1000) / 10, Math.round(yPt))
              }
              style={{ zIndex: g.z || 2 }}
            >
              <div
                data-testid={`prev-gt-${kind}`}
                style={{
                  width: w, height: h, display: "flex", alignItems: "center",
                  justifyContent: g.align === "left" ? "flex-start" : g.align === "right" ? "flex-end" : "center",
                  fontFamily: fontCss(g.font_family),
                  fontSize: g.size, fontWeight: g.bold ? 700 : 400,
                  fontStyle: g.italic ? "italic" : "normal",
                  textDecoration: g.underline ? "underline" : "none",
                  color: g.color, letterSpacing: g.letter_spacing || 0,
                  background: g.bg_color ? hexToRgba(g.bg_color, (g.bg_opacity ?? 100) / 100) : "transparent",
                  border: (g.border_thickness || 0) > 0 ? `${g.border_thickness}px solid ${g.border_color || "#334155"}` : "none",
                  padding: g.padding || 0,
                  whiteSpace: "nowrap", overflow: "hidden", pointerEvents: "none",
                }}
              >
                {gtext}
              </div>
            </Draggable>
          );
        })}

        {/* static context (not part of the editor) */}
        {format === 1 ? <StaticContextF1 W={W} H={H} /> : !body?.extras && <StaticContextF2 W={W} H={H} />}
        {/* Format 2 extras — percentage / grade / result / prepared by / signatory (independent text elements) */}
        {fmtKey === "f2" && body?.extras && ["percentage", "grade", "result", "certificate_no", "issue_date", "prepared_by", "signatory"].map((kind) => {
          const g = body.extras[kind] || {};
          if (g.visible === false) return null;
          const w = g.width || 120;
          const h = g.height || 18;
          const samples = {
            percentage: `${SAMPLE_F2.percentage}`, grade: SAMPLE_F2.grade, result: SAMPLE_F2.result,
            certificate_no: SAMPLE_F2.certificate_number || "LI-2026-0001",
            issue_date: SAMPLE_F2.issue_date || "22 Sep 2026",
          };
          const etext = (g.text || EX_DEFAULTS[kind]).replace("{value}", samples[kind] || "");
          return (
            <Draggable key={kind} id={`ex:${kind}`} x={(g.x / 100) * W - w / 2} y={g.y ?? 600} scale={scale}
              selected={selected === `ex:${kind}`} onSelect={onSelect} disabled={g.locked}
              onMove={(id, xPt, yPt) => onMoveExtra(kind, Math.round(((xPt + w / 2) / W) * 1000) / 10, Math.round(yPt))}
              style={{ zIndex: g.z || 2 }}
            >
              <div data-testid={`prev-ex-${kind}`} style={{ width: w, pointerEvents: "none" }}>
                {(kind === "prepared_by" || kind === "signatory") && g.show_line !== false && (
                  <div style={{ borderTop: "0.8px solid #4b5563", marginBottom: 3 }} />
                )}
                <div style={{
                  height: h, display: "flex", alignItems: "center",
                  justifyContent: g.align === "left" ? "flex-start" : g.align === "right" ? "flex-end" : "center",
                  fontFamily: fontCss(g.font_family), fontSize: g.size, fontWeight: g.bold ? 700 : 400,
                  fontStyle: g.italic ? "italic" : "normal", textDecoration: g.underline ? "underline" : "none",
                  color: g.color, letterSpacing: g.letter_spacing || 0,
                  background: g.bg_color ? hexToRgba(g.bg_color, (g.bg_opacity ?? 100) / 100) : "transparent",
                  border: (g.border_thickness || 0) > 0 ? `${g.border_thickness}px solid ${g.border_color || "#334155"}` : "none",
                  padding: g.padding || 0, whiteSpace: "nowrap", overflow: "hidden",
                }}>
                  {etext}
                </div>
              </div>
            </Draggable>
          );
        })}

        {/* QR code — independent element — Format 2 */}
        {fmtKey === "f2" && body?.extras?.qr && body.extras.qr.visible !== false && (() => {
          const q = body.extras.qr;
          const size = q.size || 54;
          return (
            <Draggable id="ex:qr" x={(q.x / 100) * W - size / 2} y={q.y ?? 754} scale={scale}
              selected={selected === "ex:qr"} onSelect={onSelect} disabled={q.locked}
              onMove={(id, xPt, yPt) => onMoveQR(Math.round(((xPt + size / 2) / W) * 1000) / 10, Math.round(yPt))}
              style={{ zIndex: q.z || 2 }}
            >
              <div data-testid="prev-ex-qr" style={{ width: size, pointerEvents: "none", textAlign: "center" }}>
                <div style={{
                  width: size, height: size, display: "grid", placeItems: "center",
                  background: "#fff", border: "1.5px solid #0f172a",
                  backgroundImage: "repeating-conic-gradient(#0f172a 0% 25%, #fff 0% 50%)",
                  backgroundSize: "8px 8px",
                }}>
                  <span style={{ background: "#fff", padding: "1px 4px", color: "#0f172a", fontSize: Math.max(9, size / 5), fontWeight: 700, letterSpacing: 1 }}>QR</span>
                </div>
                {q.show_caption !== false && (
                  <div style={{ fontSize: 7, color: "#4b5563", marginTop: 2 }}>{q.caption || "Scan to verify"}</div>
                )}
              </div>
            </Draggable>
          );
        })()}

        {/* signature / prepared-by signature / stamp — independent image elements — Format 2 */}
        {fmtKey === "f2" && body?.extras && [
          ["signature_img", sigAssets?.signature, "Signature"],
          ["prepared_sign_img", sigAssets?.prepared_signature, "Prepared-by signature"],
          ["stamp_img", sigAssets?.stamp, "Stamp"],
        ].map(([kind, src, ph]) => {
          const g = body.extras[kind] || {};
          if (g.visible === false) return null;
          const w = g.width || 100;
          const h = g.height || 60;
          return (
            <Draggable key={kind} id={`ex:${kind}`} x={(g.x / 100) * W - w / 2} y={g.y ?? 680} scale={scale}
              selected={selected === `ex:${kind}`} onSelect={onSelect} disabled={g.locked}
              onMove={(id, xPt, yPt) => onMoveExtra(kind, Math.round(((xPt + w / 2) / W) * 1000) / 10, Math.round(yPt))}
              style={{ zIndex: g.z || 2 }}
            >
              <div data-testid={`prev-ex-${kind}`} style={{
                width: w, height: h, pointerEvents: "none",
                opacity: (g.opacity ?? 100) / 100,
                transform: g.rotation ? `rotate(${g.rotation}deg)` : "none",
              }}>
                {src ? (
                  <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                ) : (
                  <div style={{
                    width: "100%", height: "100%", border: "1px dashed #94a3b8", display: "grid",
                    placeItems: "center", fontSize: 9, color: "#94a3b8", textAlign: "center", lineHeight: 1.3,
                  }}>
                    {ph}<br />(upload in Templates)
                  </div>
                )}
              </div>
            </Draggable>
          );
        })}

        {/* Grade scale strip — independent element — Format 2 */}
        {fmtKey === "f2" && body?.grade_scale && body.grade_scale.visible !== false && (() => {
          const gs = body.grade_scale;
          const w = gs.width || 507;
          const h = gs.height || 30;
          const rows = gs.rows?.length ? gs.rows : DEFAULT_GS_ROWS;
          return (
            <Draggable id="gscale" x={(gs.x / 100) * W - w / 2} y={gs.y ?? 660} scale={scale}
              selected={selected === "gscale"} onSelect={onSelect} disabled={gs.locked}
              onMove={(id, xPt, yPt) => onMoveGradeScale(Math.round(((xPt + w / 2) / W) * 1000) / 10, Math.round(yPt))}
              style={{ zIndex: gs.z || 1 }}
            >
              <div data-testid="prev-gscale" style={{
                width: w, height: h, display: "flex", alignItems: "center", gap: gs.gap ?? 18,
                background: gs.bg || "#f1f5f9",
                border: (gs.border_thickness ?? 0.7) > 0 ? `${gs.border_thickness}px solid ${gs.border_color || "#334155"}` : "none",
                padding: `0 ${gs.padding ?? 10}px`, pointerEvents: "none", overflow: "hidden",
                fontFamily: fontCss(gs.font_family), fontSize: gs.size || 9.5, color: gs.color || "#111827",
              }}>
                <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{gs.label ?? "Grade Scale :"}</span>
                {rows.map((r, i) => (
                  <span key={r[0] || i} style={{ whiteSpace: "nowrap", fontWeight: gs.bold ? 700 : 400 }}>{r[0]}{r[1] ? ` : ${r[1]}` : ""}</span>
                ))}
              </div>
            </Draggable>
          );
        })()}

      </div>
    </div>
  );
}

function StaticContextF1({ W, H }) {
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ color: "#cbd5e1" }}>
      <div className="absolute w-full text-center" style={{ top: 170, fontSize: 34, fontWeight: 700 }}>CERTIFICATE</div>
      <div className="absolute text-[10px]" style={{ left: 48, bottom: 40 }}>QR</div>
      <div className="absolute text-[10px] font-semibold" style={{ right: 60, bottom: 40 }}>Authorized Signatory</div>
    </div>
  );
}

function StaticContextF2({ W, H }) {
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ color: "#cbd5e1" }}>
      <div className="absolute text-[10px]" style={{ left: 44, bottom: 150 }}>Percentage · Grade · Result</div>
      <div className="absolute text-[9px]" style={{ left: 44, bottom: 96 }}>Prepared by</div>
      <div className="absolute text-[9px]" style={{ right: 60, bottom: 96 }}>Authorized Signatory</div>
    </div>
  );
}

function Draggable({ id, x, y, scale, selected, onSelect, onMove, style, children, disabled }) {
  const drag = useRef(null);
  const onDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(id);
    if (disabled) return;
    drag.current = { sx: e.clientX, sy: e.clientY, ox: x, oy: y };
    const mv = (ev) => {
      const d = drag.current;
      if (!d) return;
      onMove(id, d.ox + (ev.clientX - d.sx) / scale, d.oy + (ev.clientY - d.sy) / scale);
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener("pointermove", mv);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", up);
  };
  return (
    <div
      onPointerDown={onDown}
      style={{ position: "absolute", left: x, top: y, cursor: disabled ? "default" : "move", ...style }}
      className={selected ? "ring-2 ring-blue-500 ring-offset-1 rounded" : "hover:ring-1 hover:ring-blue-300 rounded"}
    >
      {children}
    </div>
  );
}

function TableControls({ t, onChange, onCol, onMoveCol, idPrefix }) {
  return (
    <div className="space-y-3">
      <div className="text-[11px] font-medium text-slate-500">Table position & size</div>
      <div className="grid grid-cols-3 gap-2">
        <MiniField label="X center (%)">
          <Input type="number" data-testid={`${idPrefix}-x`} value={t.x} className="h-8 text-xs"
            onChange={(e) => onChange({ x: Number(e.target.value) })} />
        </MiniField>
        <MiniField label="Y top (pt)">
          <Input type="number" data-testid={`${idPrefix}-y`} value={t.y} className="h-8 text-xs"
            onChange={(e) => onChange({ y: Number(e.target.value) })} />
        </MiniField>
        <MiniField label="Width (pt)">
          <Input type="number" data-testid={`${idPrefix}-width`} value={t.width} className="h-8 text-xs"
            onChange={(e) => onChange({ width: Number(e.target.value) })} />
        </MiniField>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <MiniField label="Row height">
          <Input type="number" data-testid={`${idPrefix}-row-height`} value={t.row_height} className="h-8 text-xs"
            onChange={(e) => onChange({ row_height: Number(e.target.value) })} />
        </MiniField>
        <MiniField label="Cell padding">
          <Input type="number" data-testid={`${idPrefix}-cell-padding`} value={t.cell_padding} className="h-8 text-xs"
            onChange={(e) => onChange({ cell_padding: Number(e.target.value) })} />
        </MiniField>
        <MiniField label="Table background">
          <div className="flex items-center gap-1.5">
            <Input type="color" data-testid={`${idPrefix}-bg`} value={t.table_bg || "#ffffff"} className="h-8 w-10"
              onChange={(e) => onChange({ table_bg: e.target.value })} />
            <label className="flex items-center gap-1 text-[11px] text-slate-500">
              <input type="checkbox" data-testid={`${idPrefix}-bg-none`} checked={!t.table_bg}
                onChange={(e) => onChange({ table_bg: e.target.checked ? "" : "#ffffff" })} />
              None
            </label>
          </div>
        </MiniField>
      </div>

      <div className="rounded-lg border border-slate-200 p-2.5">
        <div className="text-[11px] font-medium text-slate-500 mb-1.5">Heading style</div>
        <MiniStyle value={t.header} idPrefix={`${idPrefix}-th`} withBg
          onChange={(p) => onChange({ header: { ...t.header, ...p } })} />
      </div>
      <div className="rounded-lg border border-slate-200 p-2.5">
        <div className="text-[11px] font-medium text-slate-500 mb-1.5">Cell style</div>
        <MiniStyle value={t.cell} idPrefix={`${idPrefix}-tc`} withBg
          onChange={(p) => onChange({ cell: { ...t.cell, ...p } })} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <MiniField label="Total row background">
          <Input type="color" data-testid={`${idPrefix}-trow-bg`} value={t.total_row.bg} className="h-8 w-full"
            onChange={(e) => onChange({ total_row: { ...t.total_row, bg: e.target.value } })} />
        </MiniField>
        <MiniField label="Border color">
          <Input type="color" data-testid={`${idPrefix}-border-color`} value={t.borders.color} className="h-8 w-full"
            onChange={(e) => onChange({ borders: { ...t.borders, color: e.target.value } })} />
        </MiniField>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <MiniField label="Border style">
          <Select value={t.borders.style}
            onValueChange={(v) => onChange({ borders: { ...t.borders, style: v } })}>
            <SelectTrigger data-testid={`${idPrefix}-border-style`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="solid">Solid</SelectItem>
              <SelectItem value="dashed">Dashed</SelectItem>
              <SelectItem value="dotted">Dotted</SelectItem>
            </SelectContent>
          </Select>
        </MiniField>
        <MiniField label="Thickness">
          <Input type="number" step="0.1" data-testid={`${idPrefix}-border-thickness`} value={t.borders.thickness} className="h-8 text-xs"
            onChange={(e) => onChange({ borders: { ...t.borders, thickness: Number(e.target.value) } })} />
        </MiniField>
        <MiniField label="Borders">
          <div className="flex items-center gap-2 text-[11px] text-slate-600 h-8">
            <label className="flex items-center gap-1">
              <input type="checkbox" data-testid={`${idPrefix}-border-outer`} checked={t.borders.outer !== false}
                onChange={(e) => onChange({ borders: { ...t.borders, outer: e.target.checked } })} />
              Outer
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" data-testid={`${idPrefix}-border-inner`} checked={t.borders.inner !== false}
                onChange={(e) => onChange({ borders: { ...t.borders, inner: e.target.checked } })} />
              Inner
            </label>
          </div>
        </MiniField>
      </div>

      <div className="text-[11px] font-medium text-slate-500">Columns (order, width, alignment)</div>
      {t.columns.map((col, i) => (
        <div key={col.key} className="rounded-lg border border-slate-200 p-2.5 space-y-2">
          <div className="flex items-center gap-1.5">
            <Input value={col.label} data-testid={`${idPrefix}-col-label-${col.key}`} className="h-8 text-xs"
              onChange={(e) => onCol(col.key, { label: e.target.value })} />
            <button data-testid={`${idPrefix}-col-up-${col.key}`} disabled={i === 0} onClick={() => onMoveCol(i, -1)}
              className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move left"><ArrowUp size={13} /></button>
            <button data-testid={`${idPrefix}-col-down-${col.key}`} disabled={i === t.columns.length - 1} onClick={() => onMoveCol(i, 1)}
              className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move right"><ArrowDown size={13} /></button>
            <button data-testid={`${idPrefix}-col-visible-${col.key}`} onClick={() => onCol(col.key, { visible: col.visible === false })}
              className="p-1 text-slate-400 hover:text-slate-700" title="Show / hide column">
              {col.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <MiniField label="Width (pt)">
              <Input type="number" data-testid={`${idPrefix}-col-width-${col.key}`} value={col.width} className="h-8 text-xs"
                onChange={(e) => onCol(col.key, { width: Number(e.target.value) })} />
            </MiniField>
            <MiniField label="Heading align">
              <Select value={col.header_align} onValueChange={(v) => onCol(col.key, { header_align: v })}>
                <SelectTrigger data-testid={`${idPrefix}-col-halign-${col.key}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </MiniField>
            <MiniField label="Cell align">
              <Select value={col.cell_align} onValueChange={(v) => onCol(col.key, { cell_align: v })}>
                <SelectTrigger data-testid={`${idPrefix}-col-calign-${col.key}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </MiniField>
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniStyle({ value, onChange, idPrefix, withBg }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <MiniField label="Font">
          <FontSelect value={value.font_family} onChange={(v) => onChange({ font_family: v })} testId={`${idPrefix}-font`} />
        </MiniField>
        <MiniField label="Size">
          <Input type="number" min={6} max={144} data-testid={`${idPrefix}-size`} value={value.size} className="h-8 text-xs"
            onChange={(e) => onChange({ size: clampSize(e.target.value) })} />
        </MiniField>
      </div>
      <div className="flex items-center gap-1.5">
        <StyleToggle icon={<Bold size={13} />} active={value.bold} testId={`${idPrefix}-bold`}
          onClick={() => onChange({ bold: !value.bold })} title="Bold" />
        <StyleToggle icon={<AlignLeft size={13} />} active={value.align === "left"} testId={`${idPrefix}-align-left`}
          onClick={() => onChange({ align: "left" })} title="Left" />
        <StyleToggle icon={<AlignCenter size={13} />} active={value.align === "center"} testId={`${idPrefix}-align-center`}
          onClick={() => onChange({ align: "center" })} title="Center" />
        <StyleToggle icon={<AlignRight size={13} />} active={value.align === "right"} testId={`${idPrefix}-align-right`}
          onClick={() => onChange({ align: "right" })} title="Right" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <MiniField label="Text color">
          <Input type="color" data-testid={`${idPrefix}-color`} value={value.color} className="h-8 w-full"
            onChange={(e) => onChange({ color: e.target.value })} />
        </MiniField>
        {withBg && (
          <MiniField label="Background">
            <div className="flex items-center gap-1.5">
              <Input type="color" data-testid={`${idPrefix}-bg`} value={value.bg || "#ffffff"} className="h-8 w-12"
                onChange={(e) => onChange({ bg: e.target.value })} />
              <label className="flex items-center gap-1 text-[11px] text-slate-500">
                <input type="checkbox" data-testid={`${idPrefix}-bg-none`} checked={!value.bg}
                  onChange={(e) => onChange({ bg: e.target.checked ? "" : "#e8eef9" })} />
                None
              </label>
            </div>
          </MiniField>
        )}
      </div>
    </div>
  );
}

/* ---------------- controls ---------------- */

function Panel({ icon, title, badge, action, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-blue-800">{icon}</span>
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {badge && <span className="text-[10px] bg-slate-200 text-slate-600 rounded px-1.5 py-0.5">{badge}</span>}
        <div className="flex-1" />
        {action}
      </div>
      {children}
    </section>
  );
}

function MiniField({ label, children }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function TextControls({ value, onChange, textValue, onTextChange, textPlaceholder, idPrefix, compact }) {
  const el = value;
  return (
    <div className="space-y-2">
      {onTextChange && (
        <MiniField label="Text (empty = from Settings)">
          <Input value={textValue || ""} data-testid={`${idPrefix}-text`} className="h-8 text-xs"
            placeholder={textPlaceholder} onChange={(e) => onTextChange(e.target.value)} />
        </MiniField>
      )}
      <div className="grid grid-cols-2 gap-2">
        <MiniField label="Font family">
          <FontSelect value={el.font_family} onChange={(v) => onChange({ font_family: v })} testId={`${idPrefix}-font`} />
        </MiniField>
        <MiniField label="Color">
          <Input type="color" data-testid={`${idPrefix}-color`} value={el.color} className="h-8 w-full"
            onChange={(e) => onChange({ color: e.target.value })} />
        </MiniField>
      </div>
      <MiniField label="Font size">
        <div className="flex items-center gap-1 flex-wrap">
          {SIZES.map((s) => (
            <button key={s} type="button" data-testid={`${idPrefix}-size-${s}`} onClick={() => onChange({ size: s })}
              className={`px-1.5 py-0.5 rounded text-[11px] transition-soft ${
                el.size === s ? "bg-blue-800 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-blue-300"
              }`}>
              {s}
            </button>
          ))}
          <Input type="number" min={6} max={144} data-testid={`${idPrefix}-size-custom`} value={el.size} className="h-7 w-16 text-xs"
            onChange={(e) => onChange({ size: clampSize(e.target.value) })} />
        </div>
      </MiniField>
      <div className="flex items-center gap-1.5">
        <StyleToggle icon={<Bold size={13} />} active={el.bold} testId={`${idPrefix}-bold`}
          onClick={() => onChange({ bold: !el.bold })} title="Bold" />
        <StyleToggle icon={<Italic size={13} />} active={el.italic} testId={`${idPrefix}-italic`}
          onClick={() => onChange({ italic: !el.italic })} title="Italic" />
        <StyleToggle icon={<Underline size={13} />} active={el.underline} testId={`${idPrefix}-underline`}
          onClick={() => onChange({ underline: !el.underline })} title="Underline" />
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <StyleToggle icon={<AlignLeft size={13} />} active={el.align === "left"} testId={`${idPrefix}-align-left`}
          onClick={() => onChange({ align: "left" })} title="Left" />
        <StyleToggle icon={<AlignCenter size={13} />} active={el.align === "center"} testId={`${idPrefix}-align-center`}
          onClick={() => onChange({ align: "center" })} title="Center" />
        <StyleToggle icon={<AlignRight size={13} />} active={el.align === "right"} testId={`${idPrefix}-align-right`}
          onClick={() => onChange({ align: "right" })} title="Right" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <MiniField label="Text background">
          <div className="flex items-center gap-1.5">
            <Input type="color" data-testid={`${idPrefix}-bg`} value={el.bg_color || "#ffffff"} className="h-8 w-12"
              onChange={(e) => onChange({ bg_color: e.target.value })} />
            <label className="flex items-center gap-1 text-[11px] text-slate-500">
              <input type="checkbox" data-testid={`${idPrefix}-bg-transparent`}
                checked={!el.bg_color}
                onChange={(e) => onChange({ bg_color: e.target.checked ? "" : "#dbeafe" })} />
              Transparent
            </label>
          </div>
        </MiniField>
        {el.bg_color ? (
          <MiniField label={`Background opacity — ${el.bg_opacity}%`}>
            <input type="range" min="10" max="100" value={el.bg_opacity} data-testid={`${idPrefix}-bg-opacity`}
              onChange={(e) => onChange({ bg_opacity: Number(e.target.value) })} className="w-full accent-blue-800" />
          </MiniField>
        ) : (
          <MiniField label="Letter spacing">
            <Input type="number" step="0.5" data-testid={`${idPrefix}-letter-spacing`} value={el.letter_spacing} className="h-8 text-xs"
              onChange={(e) => onChange({ letter_spacing: Number(e.target.value) })} />
          </MiniField>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <MiniField label="X position (%)">
          <Input type="number" data-testid={`${idPrefix}-x`} value={el.x} className="h-8 text-xs"
            onChange={(e) => onChange({ x: Number(e.target.value) })} />
        </MiniField>
        <MiniField label="Y position (pt from top)">
          <Input type="number" data-testid={`${idPrefix}-y`} value={el.y} className="h-8 text-xs"
            onChange={(e) => onChange({ y: Number(e.target.value) })} />
        </MiniField>
      </div>
    </div>
  );
}

function StyleToggle({ icon, active, onClick, title, testId }) {
  return (
    <button type="button" title={title} data-testid={testId} onClick={onClick}
      className={`p-1.5 rounded border transition-soft ${
        active ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
      }`}>
      {icon}
    </button>
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function hexToRgba(hex, alpha) {
  try {
    const h = hex.replace("#", "");
    const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  } catch {
    return hex;
  }
}
